/**
 * PixiLayer — the 2D world layer. Renders the active map layer with sprite atlases:
 *   • "system" — star sprite, orbit rings, planet sprites.
 *   • "planet" — central planet sprite, moon orbit rings + moon/sub-moon sprites,
 *     and the scattered asteroid field.
 * Plus the mothership sprite (smaller in the system view), mining beams, drones.
 */
import {
  Application,
  Assets,
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Text,
  Texture,
} from "pixi.js";
import type { GameState, FloaterEvent } from "@/engine/GameState";
import type { Camera } from "./RenderRoot";
import {
  PERF,
  MOTHERSHIP,
  MINING_LASER,
  NPC,
  ENEMY,
  CRUISER,
  ENEMY_CARRIER,
  WORM,
  PLAYER_FIGHTER,
  FIGHTER_DRONE,
  SHIP_DEFENSE,
  SERVICE,
  TURRET,
  CANNON,
  BIG_CANNON,
  MKVI,
  STATION,
  MKVII,
  RIFT,
  COIL,
} from "@/config/balance";
import type { StationWeapon } from "@/game/npc/Station";
import { FloatingText } from "./FloatingText";
import { MineralReadouts } from "./MineralReadouts";
import { DroneField } from "./DroneField";
import { AsteroidField } from "./AsteroidField";
import { BodySpriteField, type SpriteBody } from "./BodySpriteField";
import type { MineableBody } from "@/game/world/body";
import { PLANET_FRAMES } from "./planetFrames";
import { MOON_FRAMES } from "./moonFrames";
import { STAR_FRAMES } from "./starFrames";

const EMPTY: SpriteBody[] = [];

// Hull size multiplier per tier: Mk I = 1.0, Mk II = +20%, Mk III = +40%.
// Hull render scale per tier. Mk IV is a titan — 100% bigger than Mk III.
const HULL_SIZE_MUL = [1, 1.2, 2.625, 5.25, 6.3, 7.077]; // Mk V = Carrier ×1.2; Mk VI ≈ 368 world units
// Swarm-lord thruster nozzles (lat = frac of halfW, back = frac of halfH toward the
// stern): 1 on the centre spine + 2 trailing behind each swept wing.
const SWL_THRUSTERS = [
  { lat: 0, back: 0.9 },
  { lat: -0.36, back: 0.68 },
  { lat: -0.6, back: 0.6 },
  { lat: 0.36, back: 0.68 },
  { lat: 0.6, back: 0.6 },
];
// Mk VI Battleship engine bank: a tight cluster of plumes on the bulky stern (the wide top
// of MTS_VI_BTS; the sharp end is the prow). Kept narrow + inboard so they read as engines
// welded to the hull, not a detached comb of lines across the whole rear edge.
const MKVI_BTS_THRUSTERS = [
  { lat: 0, back: 0.8 }, // main central engine
  { lat: -0.16, back: 0.74 },
  { lat: 0.16, back: 0.74 },
  { lat: -0.31, back: 0.63 },
  { lat: 0.31, back: 0.63 },
];
// Mk VI Swarm-lord engines. PROVISIONAL — replace with the mapper's "thruster" group once mapped
// (convert each pixel offset on MTS_VI_SWL 1016×1497: lat = ox/508, back = -oy/748.5).
const MKVI_SWL_THRUSTERS = [
  { lat: 0, back: 0.9 }, // central spine engine
  { lat: -0.3, back: 0.82 },
  { lat: 0.3, back: 0.82 },
  { lat: -0.62, back: 0.66 }, // wing engines
  { lat: 0.62, back: 0.66 },
];
// Mk VII Swarm-lord engines — 7 mapped nozzles (MKVIISWL Mapping.txt: lat=ox/512, back=-oy/768).
const MKVII_SWL_THRUSTERS = [
  { lat: 0.004, back: 0.888 }, // central spine engine
  { lat: -0.281, back: 0.685 },
  { lat: 0.281, back: 0.685 },
  { lat: -0.484, back: 0.547 },
  { lat: 0.484, back: 0.547 },
  { lat: -0.771, back: 0.458 }, // outer wing engines
  { lat: 0.771, back: 0.458 },
];
// Shield-bubble radius per hull tier lives in SHIP_DEFENSE.shieldBubbleMul so the
// VFX and the sim's incoming-fire hitbox stay in sync.

// Seconds an asteroid-explosion ring expands before fading out.
const RING_LIFE = 0.6;
const BIG_RING_LIFE = 1.3; // planetary (moon) explosion lasts longer
// Enemy sprite pool: regular raiders + headroom for cruiser jump-in reinforcements.
const ENEMY_RENDER_MAX =
  ENEMY.maxCount +
  CRUISER.backupSquadMax * CRUISER.maxPerLayer +
  // headroom for a carrier's launched wing (6) + its own jump-in backup squad
  (ENEMY_CARRIER.fighters + ENEMY_CARRIER.backupSquadMax) * ENEMY_CARRIER.maxPerLayer;

export class PixiLayer {
  app = new Application();
  private initialized = false;
  private world = new Container();
  private starField = new Container(); // parallax starfield behind the world
  private gOrbits = new Graphics();
  private gShip = new Graphics();
  private shipSprite?: Sprite;
  private shipTextures: (Texture | undefined)[] = []; // [Mk I, Mk II, Mk III, Mk IV]
  private baseShipScales = [1, 1, 1, 1];
  private remoteShips: Sprite[] = []; // Central Hub: OTHER pilots' ship sprites (pool)
  private remoteWormHeads: Sprite[] = []; // hub: remote MK VII/VIII worm pilots — head sprites (pool)
  private remoteWormBodies: Sprite[] = []; // hub: their body segments (shared pool)
  private remoteWormTails: Sprite[] = []; // hub: their tail sprites (pool)
  private gRemoteThrust = new Graphics(); // hub: remote pilots' thruster plumes
  private shipTexVBts?: Texture; // Mk V Battleship hull
  private shipTexVSwl?: Texture; // Mk V Swarm-lord hull
  private shipTexVIBts?: Texture; // Mk VI Battleship hull
  private shipTexVISwl?: Texture; // Mk VI Swarm-lord hull
  private shipTexVIISwl?: Texture; // Mk VII Swarm-lord hull (cosmic)
  private baseScaleVBts = 1;
  private baseScaleVSwl = 1;
  private baseScaleVIBts = 1;
  private baseScaleVISwl = 1;
  private baseScaleVIISwl = 1;
  private hullPath: "battleship" | "swarmlord" | "none" = "none";
  private hullTier = -1; // forces the initial texture assignment
  private hullMkviiSwl = false; // tracks the Mk VII SWL variant (same tier/path as Mk VI SWL)
  private starSprite = new Sprite();
  private starTextures: Texture[] = [];
  private droneField = new DroneField();
  private repairField = new DroneField(); // mechanical repair drones (same sprite)
  private gRepairFx = new Graphics(); // green twin repair beams drone → hull
  private asteroidField = new AsteroidField();
  private planetField = new BodySpriteField(
    `${import.meta.env.BASE_URL}sprites/planets.png`,
    PLANET_FRAMES,
  );
  private moonField = new BodySpriteField(
    `${import.meta.env.BASE_URL}sprites/moons.png`,
    MOON_FRAMES,
  );
  private lastCtx: unknown = null; // release pooled sprites on layer change
  private moonBodies: SpriteBody[] = []; // reused scratch list
  private aliveAsteroids: MineableBody[] = []; // reused scratch (ore-bearing only)
  private floaters = new FloatingText(); // mining ticks / XP / level-up labels
  private readouts = new MineralReadouts(); // live "minerals left" on mined bodies
  private npcLayer = new Container(); // competitor NPC hulls (below the player)
  private gNpc = new Graphics(); // NPC beams / shields / HP bars
  private npcSprites: Sprite[] = [];
  private enemyLayer = new Container(); // hostile raider hulls + red turrets
  private gEnemy = new Graphics(); // enemy red bolts / shields / HP bars
  private enemySprites: Sprite[] = [];
  private enemyTurretSprites: Sprite[] = [];
  private enemyTex?: Texture;
  private cruiserSprites: Sprite[] = []; // capital-ship hulls
  private cruiserTurretSprites: Sprite[] = []; // their rotating side turrets
  private cruiserTex?: Texture;
  private gCruiser = new Graphics(); // cruiser bolts / shields / HP bars
  private carrierSprites: Sprite[] = []; // capital-ship carrier hulls
  private carrierTurretSprites: Sprite[] = []; // their rotating turrets
  private carrierTex?: Texture;
  private gCarrier = new Graphics(); // carrier bolts / shields / HP bars / launch VFX
  private wormBodySprites: Sprite[] = []; // Eater of Worlds body segments
  private wormHeadSprite?: Sprite;
  private wormTailSprite?: Sprite;
  private wormHeadTex?: Texture;
  private wormBodyTex?: Texture;
  private wormTailTex?: Texture;
  private wormHeadTex8?: Texture; // MK VIII mechanical worm (falls back to the EOW art)
  private wormBodyTex8?: Texture;
  private wormTailTex8?: Texture;
  private gWorm = new Graphics(); // worm beam / charge glow / bolts / wreck fire / HP bars
  private playerWormHead?: Sprite; // MK VII: the player's worm hull (head/body/tail + turrets)
  private playerWormTail?: Sprite;
  private playerWormBodySprites: Sprite[] = [];
  private playerWormTurretSprites: Sprite[] = [];
  private riftClosedTex?: Texture; // Mk VIII Time & Space Rift portal art
  private riftOpenedTex?: Texture;
  private playerRiftEntry?: Sprite; // rift the worm dives into (in front of the head)
  private playerRiftExit?: Sprite; // rift the worm emerges from (at the minimap target)
  private warpRiftSprite?: Sprite; // jump-drive cutscene rift portal (system view)
  private gWarpFx = new Graphics(); // jump-drive cutscene: spaghettified planet/sun streaks
  private gPlayerWorm = new Graphics(); // MK VII maw beam + turret bolts + muzzle flashes
  private explodedPlanetTextures: Texture[] = []; // Exploded_planet_sheet frames (4×4)
  private explodedPlanetSprite?: Sprite; // scattered planet shown during the Eater's awakening
  private eaterMapIcon?: Sprite; // EOW head marker over its planet on the system map
  private pFighterSprites: Sprite[] = []; // player escort fighters
  private pFighterTex?: Texture;
  private gPFighter = new Graphics(); // friendly bolts / shields / HP bars
  private pDroneSprites: Sprite[] = []; // Swarm-lord fighter drones (0.6× fighters)
  private gPDrone = new Graphics(); // drone bolts / shields / HP bars
  private gMissile = new Graphics(); // homing-missile heads + exhaust ribbons
  private gFighterMissile = new Graphics(); // Mk VI SWL: escort-fighter wing missiles
  private gWaypoint = new Graphics(); // click-to-move: path line + destination marker
  private gRiftSwarm = new Graphics(); // Mk VII SWL Rift Swarm: purple homing arrows + trails
  private riftPortalSprites: Sprite[] = []; // Mk VII SWL Rift Swarm: EOW rift portal sprites
  private gThrustPlayer = new Graphics(); // mothership engine plumes (additive), under the hull
  private gThrustNpc = new Graphics(); // enemy/cruiser/fighter engine plumes + wing contrails
  private gMoonTurret = new Graphics(); // rogue-planet moon turrets + their red bolts
  private gTutorial = new Graphics(); // tutorial: pulse rings on safe planets
  private serviceLayer = new Container(); // trade/mechanical planet icons (system view)
  private serviceIcons: Text[] = [];
  private turretLayer = new Container(); // hull-mounted turrets
  private turretSprites: Sprite[] = [];
  private turretTex?: Texture;
  private gTurretFx = new Graphics(); // bullets + muzzle flashes
  private cannonSprites: Sprite[] = []; // Mk IV spinal cannons
  private cannonTex?: Texture;
  private gCannonFx = new Graphics(); // cannon bolts + muzzle flash + crosshair
  private bigCannonSprites: Sprite[] = []; // Mk V Battleship big cannons
  private gBigCannonFx = new Graphics(); // big-cannon beams + muzzle flash
  private stationOuter = new Sprite(); // boss ring station — outer ring (spins CW)
  private stationInner = new Sprite(); // boss ring station — inner ring (spins CCW)
  private gStation = new Graphics(); // ring-station shields / hangar glow / wreck fire
  private stationWeaponLayer = new Container(); // pooled turret/cannon sprites on the rings
  private stationWeaponSprites: Sprite[] = [];
  private rings: {
    x: number;
    y: number;
    radius: number;
    age: number;
    big: boolean;
    blue?: boolean; // homing-missile blast → blue palette
  }[] = [];
  private lastRenderMs = performance.now(); // wall-clock dt for VFX

  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }
  get ready(): boolean {
    return this.initialized;
  }

  async init(width: number, height: number, dpr: number): Promise<void> {
    await this.app.init({
      width,
      height,
      backgroundAlpha: 1,
      background: 0x05070f,
      antialias: true,
      resolution: Math.min(dpr, PERF.maxPixelRatio),
      autoDensity: true,
      powerPreference: "high-performance",
      autoStart: false,
      sharedTicker: false,
    });
    await this.asteroidField.load();
    await this.droneField.load();
    await this.repairField.load();
    await this.planetField.load();
    await this.moonField.load();

    // Star atlas → one texture per frame.
    const base = import.meta.env.BASE_URL;
    const starSheet: Texture = await Assets.load(`${base}sprites/stars.png`);
    this.starTextures = STAR_FRAMES.map(
      (f) =>
        new Texture({
          source: starSheet.source,
          frame: new Rectangle(f.x, f.y, f.w, f.h),
        }),
    );
    this.starSprite.anchor.set(0.5);
    this.starSprite.visible = false;

    // Exploded-planet atlas (4×4) → one texture per frame; shown when the Eater awakens.
    const exSheet: Texture = await Assets.load(`${base}sprites/Exploded_planet_sheet.png`);
    for (let r = 0; r < 4; r++) {
      for (let col = 0; col < 4; col++) {
        this.explodedPlanetTextures.push(
          new Texture({
            source: exSheet.source,
            frame: new Rectangle(col * 256, r * 256, 256, 256),
          }),
        );
      }
    }
    const exProc = new Sprite(this.explodedPlanetTextures[0]!);
    exProc.anchor.set(0.5);
    exProc.visible = false;
    this.explodedPlanetSprite = exProc;

    this.world.addChild(
      this.gOrbits,
      this.starSprite,
      this.planetField.view,
      exProc,
      this.stationInner, // boss ring station sits around the planet, behind moons/ships
      this.stationOuter,
      this.stationWeaponLayer, // turret/cannon sprites on the ring bands
      this.gStation, // shields / hangar glow / wreck fire over the weapons
      this.moonField.view,
      this.asteroidField.view,
      this.gMoonTurret, // moon turrets sit above the moons, below the player hull
      this.droneField.view,
      this.gShip,
    );
    this.app.stage.addChild(this.world);

    // Starfield: scattered dots on a big field, drawn behind the world and moved
    // at a fraction of the camera (parallax) so space feels deep.
    const stars = new Graphics();
    for (let i = 0; i < 340; i++) {
      const x = (Math.random() * 2 - 1) * 4200;
      const y = (Math.random() * 2 - 1) * 4200;
      const r = Math.random() * 1.8 + 0.4;
      const alpha = Math.random() * 0.55 + 0.25;
      const roll = Math.random();
      const color = roll < 0.12 ? 0x9fc6ff : roll < 0.2 ? 0xffe6c0 : 0xffffff;
      stars.circle(x, y, r).fill({ color, alpha });
    }
    this.starField.addChild(stars);
    this.app.stage.addChildAt(this.starField, 0); // behind the world

    // Hull tiers: MTS_1 (base) → MTS_2 (Mk II) → MTS_3 (Mk III), swapped by upgrade.
    const hullFiles = ["MTS_1.png", "MTS_2.png", "MTS_3.png", "MTS_4.png"];
    for (let i = 0; i < hullFiles.length; i++) {
      const t: Texture = await Assets.load(`${base}sprites/${hullFiles[i]}`);
      this.shipTextures[i] = t;
      this.baseShipScales[i] = MOTHERSHIP.spriteWorldHeight / t.height;
    }
    // Mk V hulls — two paths, loaded separately (fall back to Mk IV if missing).
    try {
      this.shipTexVBts = await Assets.load(`${base}sprites/MTS_V_BTS.png`);
      this.baseScaleVBts = MOTHERSHIP.spriteWorldHeight / this.shipTexVBts!.height;
    } catch {
      this.shipTexVBts = this.shipTextures[3];
      this.baseScaleVBts = this.baseShipScales[3];
    }
    try {
      this.shipTexVSwl = await Assets.load(`${base}sprites/MTS_V_SWL.png`);
      this.baseScaleVSwl = MOTHERSHIP.spriteWorldHeight / this.shipTexVSwl!.height;
    } catch {
      this.shipTexVSwl = this.shipTextures[3];
      this.baseScaleVSwl = this.baseShipScales[3];
    }
    // Mk VI Battleship hull (falls back to the Mk V Battleship texture if missing).
    try {
      this.shipTexVIBts = await Assets.load(`${base}sprites/MTS_VI_BTS.png`);
      this.baseScaleVIBts = MOTHERSHIP.spriteWorldHeight / this.shipTexVIBts!.height;
    } catch {
      this.shipTexVIBts = this.shipTexVBts;
      this.baseScaleVIBts = this.baseScaleVBts;
    }
    // Mk VI Swarm-lord hull (falls back to the Mk V Swarm-lord texture until the art exists).
    try {
      this.shipTexVISwl = await Assets.load(`${base}sprites/MTS_VI_SWL.png`);
      this.baseScaleVISwl = MOTHERSHIP.spriteWorldHeight / this.shipTexVISwl!.height;
    } catch {
      this.shipTexVISwl = this.shipTexVSwl;
      this.baseScaleVISwl = this.baseScaleVSwl;
    }
    // Mk VII Swarm-lord hull (cosmic; falls back to the Mk VI Swarm-lord texture until the art exists).
    try {
      this.shipTexVIISwl = await Assets.load(`${base}sprites/MTS_VII_SWL.png`);
      this.baseScaleVIISwl = MOTHERSHIP.spriteWorldHeight / this.shipTexVIISwl!.height;
    } catch {
      this.shipTexVIISwl = this.shipTexVISwl;
      this.baseScaleVIISwl = this.baseScaleVISwl;
    }
    // Boss ring station — one sprite reused for both counter-rotating rings.
    try {
      const stationTex: Texture = await Assets.load(`${base}sprites/Enemy_Station.png`);
      this.stationOuter.texture = stationTex;
      this.stationInner.texture = stationTex;
    } catch {
      /* sprite missing → the rings just stay invisible */
    }
    // Anchor on the RING'S true centre (its hole sits ~2% high in the 1024×1536 frame),
    // so the ring is exactly concentric with the planet.
    this.stationOuter.anchor.set(0.5, 0.482);
    this.stationInner.anchor.set(0.5, 0.482);
    this.stationOuter.visible = false;
    this.stationInner.visible = false;
    // Pool of weapon sprites (2 rings × 36 guns = 72, + headroom) reusing the player's
    // turret/cannon textures so the station guns match the player's exactly.
    for (let i = 0; i < 96; i++) {
      const s = new Sprite();
      s.anchor.set(0.5, 0.42);
      s.visible = false;
      this.stationWeaponLayer.addChild(s);
      this.stationWeaponSprites.push(s);
    }
    // Competitor NPC hulls (MTS_1) live in their own layer below the player.
    for (let i = 0; i < NPC.count; i++) {
      const sp = new Sprite(this.shipTextures[0]);
      sp.anchor.set(0.5);
      sp.visible = false;
      this.npcLayer.addChild(sp);
      this.npcSprites.push(sp);
    }
    this.npcLayer.addChild(this.gNpc);
    this.world.addChild(this.npcLayer);

    // Hostile raider hulls (their own sprite). The enemyLayer is added to the
    // world LAST (below) so raiders render ON TOP of the player on a fly-past.
    const enemyTex: Texture = await Assets.load(`${base}sprites/enemy_ship.png`);
    this.enemyTex = enemyTex;
    for (let i = 0; i < ENEMY_RENDER_MAX; i++) {
      const sp = new Sprite(enemyTex);
      sp.anchor.set(0.5);
      sp.visible = false;
      this.enemyLayer.addChild(sp);
      this.enemySprites.push(sp);
    }

    const ship = new Sprite(this.shipTextures[0]);
    ship.anchor.set(0.5);
    this.shipSprite = ship;
    // Central Hub: pooled sprites for OTHER pilots' ships + their thrusters (under the local hull).
    this.gRemoteThrust.blendMode = "add";
    this.world.addChild(this.gRemoteThrust);
    for (let i = 0; i < 16; i++) {
      const rs = new Sprite(this.shipTextures[0]);
      rs.anchor.set(0.5);
      rs.visible = false;
      this.world.addChild(rs);
      this.remoteShips.push(rs);
    }
    // Remote MK VII/VIII worm pilots render as a head+body+tail chain. Shared pools, textured
    // per-frame in drawRemotePlayers. Added bodies→tails→heads so a head draws on top of its body
    // (matches the local worm's z-order). Sized for a handful of worm pilots; overflow degrades.
    const mkEmpty = (): Sprite => {
      const s = new Sprite(this.shipTextures[0]);
      s.anchor.set(0.5);
      s.visible = false;
      this.world.addChild(s);
      return s;
    };
    for (let i = 0; i < 112; i++) this.remoteWormBodies.push(mkEmpty()); // 8 worms × up to 14 bodies
    for (let i = 0; i < 8; i++) this.remoteWormTails.push(mkEmpty());
    for (let i = 0; i < 8; i++) this.remoteWormHeads.push(mkEmpty());
    this.gThrustPlayer.blendMode = "add"; // engine glow adds onto space
    this.world.addChild(this.gThrustPlayer); // plume sits under the hull
    this.world.addChild(ship); // hull renders on top of the bodies

    // Turrets mount on top of the hull; their bolts/flashes render above them.
    const turretTex: Texture = await Assets.load(`${base}sprites/turret_basic.png`);
    this.turretTex = turretTex;
    for (let i = 0; i < TURRET.slotsMkVI; i++) {
      const t = new Sprite(turretTex);
      t.anchor.set(0.5, 0.42); // pivot near the turret base
      t.visible = false;
      this.turretLayer.addChild(t);
      this.turretSprites.push(t);
    }
    this.world.addChild(this.turretLayer);
    this.world.addChild(this.gTurretFx);

    // Mk IV spinal cannons (their own sprite) mount on the hull centre line.
    const cannonTex: Texture = await Assets.load(`${base}sprites/cannon_gun.png`);
    this.cannonTex = cannonTex;
    for (let i = 0; i < MKVI.bts.cannons; i++) {
      // Cannon sprite pool sized to the Mk VI Battleship's 8 cannons (Mk V uses 4).
      const c = new Sprite(cannonTex);
      c.anchor.set(0.5, 0.35);
      c.visible = false;
      this.turretLayer.addChild(c);
      this.cannonSprites.push(c);
    }
    this.world.addChild(this.gCannonFx);
    // Big-cannon sprite pool (Mk VI Battleship = 4 flank guns; Mk V uses 2 on the spine).
    for (let i = 0; i < BIG_CANNON.slots; i++) {
      const c = new Sprite(cannonTex);
      c.anchor.set(0.5, 0.35);
      c.visible = false;
      this.turretLayer.addChild(c);
      this.bigCannonSprites.push(c);
    }
    this.world.addChild(this.gBigCannonFx);
    // Mechanical repair drones + their green beams render ABOVE the hull/turrets.
    this.world.addChild(this.repairField.view);
    this.world.addChild(this.gRepairFx);

    // Enemy red turrets (reuse the turret sprite) + their bolt/HP graphics.
    for (let i = 0; i < ENEMY_RENDER_MAX * 2; i++) {
      const t = new Sprite(turretTex);
      t.anchor.set(0.5, 0.42);
      t.visible = false;
      this.enemyLayer.addChild(t);
      this.enemyTurretSprites.push(t);
    }
    this.enemyLayer.addChild(this.gEnemy);

    // Capital-ship cruisers (their own big hull) + rotating side turrets.
    const cruiserTex: Texture = await Assets.load(`${base}sprites/enemy_cruiser.png`);
    this.cruiserTex = cruiserTex;
    for (let i = 0; i < CRUISER.maxPerLayer; i++) {
      const sp = new Sprite(cruiserTex);
      sp.anchor.set(0.5);
      sp.visible = false;
      this.enemyLayer.addChild(sp);
      this.cruiserSprites.push(sp);
    }
    for (let i = 0; i < CRUISER.maxPerLayer * CRUISER.mounts.length; i++) {
      const t = new Sprite(turretTex);
      t.anchor.set(0.5, 0.42);
      t.visible = false;
      this.enemyLayer.addChild(t);
      this.cruiserTurretSprites.push(t);
    }
    this.enemyLayer.addChild(this.gCruiser);

    // Capital-ship carriers (their own big evil hull). Falls back to the cruiser
    // texture (tinted red) only if the art is missing.
    let carrierTex: Texture;
    try {
      carrierTex = await Assets.load(`${base}sprites/Enemy_Carrier.png`);
    } catch {
      carrierTex = cruiserTex; // placeholder art until the carrier sprite exists
    }
    this.carrierTex = carrierTex;
    for (let i = 0; i < ENEMY_CARRIER.maxPerLayer; i++) {
      const sp = new Sprite(carrierTex);
      sp.anchor.set(0.5);
      sp.visible = false;
      this.enemyLayer.addChild(sp);
      this.carrierSprites.push(sp);
    }
    for (let i = 0; i < ENEMY_CARRIER.maxPerLayer * ENEMY_CARRIER.mounts.length; i++) {
      const t = new Sprite(turretTex);
      t.anchor.set(0.5, 0.42);
      t.visible = false;
      this.enemyLayer.addChild(t);
      this.carrierTurretSprites.push(t);
    }
    this.enemyLayer.addChild(this.gCarrier);

    // Eater of Worlds boss — a pool of body segments, then tail, then head on top.
    const loadWorm = async (name: string): Promise<Texture> => {
      try {
        return await Assets.load(`${base}sprites/${name}`);
      } catch {
        return cruiserTex; // fallback (art is bundled, so this shouldn't happen)
      }
    };
    this.wormHeadTex = await loadWorm("EOW_Head.png");
    this.wormBodyTex = await loadWorm("EOW_Body.png");
    this.wormTailTex = await loadWorm("EOW_Tail.png");
    // MK VIII mechanical worm — its own sprites, falling back to the EOW art until they exist.
    const loadWorm8 = async (name: string, fallback: Texture): Promise<Texture> => {
      try {
        return await Assets.load(`${base}sprites/${name}`);
      } catch {
        return fallback;
      }
    };
    this.wormHeadTex8 = await loadWorm8("MK_VIII_EOW_HEAD.png", this.wormHeadTex);
    this.wormBodyTex8 = await loadWorm8("MK_VIII_EOW_BODY.png", this.wormBodyTex);
    this.wormTailTex8 = await loadWorm8("MK_VIII_EOW_TAIL.png", this.wormTailTex);
    // Mk VIII rift portal art (undefined if absent → the rift render is skipped gracefully).
    const loadOpt = async (name: string): Promise<Texture | undefined> => {
      try {
        return await Assets.load(`${base}sprites/${name}`);
      } catch {
        return undefined;
      }
    };
    this.riftClosedTex = await loadOpt("CLOSED_RIFT.png");
    this.riftOpenedTex = await loadOpt("OPENED_RIFT.png");
    for (let i = 0; i < WORM.maxSegments; i++) {
      const sp = new Sprite(this.wormBodyTex);
      sp.anchor.set(0.5);
      sp.visible = false;
      this.enemyLayer.addChild(sp);
      this.wormBodySprites.push(sp);
    }
    this.wormTailSprite = new Sprite(this.wormTailTex);
    this.wormTailSprite.anchor.set(0.5);
    this.wormTailSprite.visible = false;
    this.enemyLayer.addChild(this.wormTailSprite);
    this.wormHeadSprite = new Sprite(this.wormHeadTex);
    this.wormHeadSprite.anchor.set(0.5);
    this.wormHeadSprite.visible = false;
    this.enemyLayer.addChild(this.wormHeadSprite);
    this.enemyLayer.addChild(this.gWorm);

    // Mk VIII rift portals — added first so they render BENEATH the worm (it dives into /
    // emerges from them).
    this.playerRiftEntry = new Sprite(this.riftClosedTex ?? Texture.EMPTY);
    this.playerRiftEntry.anchor.set(0.5);
    this.playerRiftEntry.visible = false;
    this.world.addChild(this.playerRiftEntry);
    this.playerRiftExit = new Sprite(this.riftClosedTex ?? Texture.EMPTY);
    this.playerRiftExit.anchor.set(0.5);
    this.playerRiftExit.visible = false;
    this.world.addChild(this.playerRiftExit);
    // Jump-drive cutscene: spaghetti streaks (behind) + the rift the ship flies through.
    this.world.addChild(this.gWarpFx);
    this.warpRiftSprite = new Sprite(this.riftClosedTex ?? Texture.EMPTY);
    this.warpRiftSprite.anchor.set(0.5);
    this.warpRiftSprite.visible = false;
    this.world.addChild(this.warpRiftSprite);
    // Mk VII SWL Rift Swarm portals — a small pool of EOW-rift sprites (closed → open), behind the arrows.
    for (let i = 0; i < 4; i++) {
      const sp = new Sprite(this.riftClosedTex ?? Texture.EMPTY);
      sp.anchor.set(0.5);
      sp.visible = false;
      this.enemyLayer.addChildAt(sp, 0);
      this.riftPortalSprites.push(sp);
    }
    // MK VII player worm — its own pool (body segments, tail, head), a turret per segment
    // on top, then the beam/bolt graphics. Reuses the EOW textures at player scale.
    for (let i = 0; i < MKVII.maxBodies; i++) {
      const seg = new Sprite(this.wormBodyTex);
      seg.anchor.set(0.5);
      seg.visible = false;
      this.world.addChild(seg);
      this.playerWormBodySprites.push(seg);
    }
    this.playerWormTail = new Sprite(this.wormTailTex);
    this.playerWormTail.anchor.set(0.5);
    this.playerWormTail.visible = false;
    this.world.addChild(this.playerWormTail);
    this.playerWormHead = new Sprite(this.wormHeadTex);
    this.playerWormHead.anchor.set(0.5);
    this.playerWormHead.visible = false;
    this.world.addChild(this.playerWormHead);
    for (let i = 0; i < MKVII.maxBodies + 2; i++) {
      const tur = new Sprite(this.wormBodyTex);
      tur.anchor.set(0.5, 0.42);
      tur.visible = false;
      this.world.addChild(tur);
      this.playerWormTurretSprites.push(tur);
    }
    this.world.addChild(this.gPlayerWorm);

    // The Eater's head, reused small as a system-map marker over its planet.
    const eIcon = new Sprite(this.wormHeadTex);
    eIcon.anchor.set(0.5);
    eIcon.visible = false;
    this.serviceLayer.addChild(eIcon);
    this.eaterMapIcon = eIcon;

    // Player escort fighters (their own blue hull) — allied, render on top.
    const pfTex: Texture = await Assets.load(`${base}sprites/player_fighter.png`);
    this.pFighterTex = pfTex;
    for (let i = 0; i < PLAYER_FIGHTER.maxSlots; i++) {
      const sp = new Sprite(pfTex);
      sp.anchor.set(0.5);
      sp.visible = false;
      this.enemyLayer.addChild(sp);
      this.pFighterSprites.push(sp);
    }
    this.enemyLayer.addChild(this.gPFighter);
    // Fighter-drone sprite pool (Swarm-lord) — same texture, rendered smaller.
    for (let i = 0; i < FIGHTER_DRONE.maxSlots; i++) {
      const sp = new Sprite(pfTex);
      sp.anchor.set(0.5);
      sp.visible = false;
      this.enemyLayer.addChild(sp);
      this.pDroneSprites.push(sp);
    }
    this.enemyLayer.addChild(this.gPDrone);
    this.enemyLayer.addChild(this.gMissile);
    this.enemyLayer.addChild(this.gWaypoint);
    this.enemyLayer.addChild(this.gRiftSwarm);
    this.enemyLayer.addChild(this.gFighterMissile);
    this.gThrustNpc.blendMode = "add"; // engine glow adds onto space
    this.enemyLayer.addChildAt(this.gThrustNpc, 0); // plumes below all fleet/hostile hulls
    this.world.addChild(this.enemyLayer); // on top of the player hull + turrets

    this.world.addChild(this.gTutorial); // tutorial highlight rings (under icons)
    this.world.addChild(this.readouts.view); // live "minerals left" labels
    this.world.addChild(this.serviceLayer); // planet service icons (system view)
    this.world.addChild(this.floaters.view); // tick numbers sit above everything

    this.initialized = true;
  }

  resize(width: number, height: number): void {
    this.app.renderer.resize(width, height);
  }

  render(state: GameState, cam: Camera, viewW: number, viewH: number): void {
    const now = performance.now();
    const dt = Math.min(0.05, (now - this.lastRenderMs) / 1000);
    this.lastRenderMs = now;

    this.world.scale.set(cam.zoom);
    this.world.position.set(
      viewW / 2 - cam.x * cam.zoom,
      viewH / 2 - cam.y * cam.zoom,
    );

    // Parallax starfield: moves at 35% of the camera → distant-star depth.
    const starP = 0.35;
    this.starField.scale.set(cam.zoom);
    this.starField.position.set(
      viewW / 2 - cam.x * cam.zoom * starP,
      viewH / 2 - cam.y * cam.zoom * starP,
    );

    // Release pooled sprites when switching layers / planet instances.
    const ctxKey = state.layer === "system" ? state.system : state.planetLayer;
    if (this.lastCtx !== ctxKey) {
      this.planetField.releaseAll();
      this.moonField.releaseAll();
      this.asteroidField.releaseAll();
      this.floaters.clear();
      this.readouts.clear();
      this.rings.length = 0;
      this.lastCtx = ctxKey;
    }

    // Drain transient VFX events + advance them.
    for (const e of state.drainExplosions()) {
      this.rings.push({
        x: e.x,
        y: e.y,
        radius: e.radius,
        age: 0,
        big: e.big ?? false,
        blue: e.blue ?? false,
      });
    }
    for (const e of state.drainFloaters()) this.spawnFloater(e);
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const r = this.rings[i]!;
      r.age += dt;
      if (r.age > (r.big ? BIG_RING_LIFE : RING_LIFE)) this.rings.splice(i, 1);
    }

    if (state.layer === "system") {
      this.gWarpFx.clear();
      if (state.warpActive && state.warpProgress > 0.001) {
        // Jump cutscene (collapse/open): the whole system spaghettifies toward the rift, so hide
        // the normal star / planets / orbits and draw streaks instead.
        this.starSprite.visible = false;
        if (this.eaterMapIcon) this.eaterMapIcon.visible = false;
        if (this.explodedPlanetSprite) this.explodedPlanetSprite.visible = false;
        this.planetField.sync(EMPTY);
        this.drawOrbits([], 0, 0, null); // clears the orbit graphics (no rings/moons)
        this.drawWarpSpaghetti(state);
      } else {
        const st = state.system.star;
        const tex =
          this.starTextures[Math.floor(st.sprite * this.starTextures.length)] ??
          this.starTextures[0];
        if (tex) {
          this.starSprite.texture = tex;
          this.starSprite.scale.set((st.radius * 2.8) / Math.max(tex.width, tex.height));
        }
        this.starSprite.position.set(st.pos.x, st.pos.y);
        this.starSprite.visible = true;

        this.drawOrbits(state.system.orbitRadii, 0, 0, null);
        this.drawSystemMoons(state.system.planets);
        this.drawStationRings(state); // rogue worlds that still host a ring-station boss
        this.syncSystemPlanets(state);
      }
      this.drawWarpRift(state); // the jump-drive portal (visible through the whole cutscene)
      this.moonField.sync(EMPTY);
      this.asteroidField.sync(EMPTY as never[]);
    } else if (state.planetLayer) {
      const L = state.planetLayer;
      this.starSprite.visible = false;
      if (this.eaterMapIcon) this.eaterMapIcon.visible = false; // head marker is map-only
      this.drawOrbits([], 0, 0, L.moons);

      const exSprite = this.explodedPlanetSprite;
      if (state.planetExploded && exSprite) {
        // Scattered planet: hide the normal hub and show an exploded frame 40% bigger.
        this.planetField.sync(EMPTY);
        const tex =
          this.explodedPlanetTextures[state.explodedFrame % this.explodedPlanetTextures.length];
        if (tex) exSprite.texture = tex;
        exSprite.visible = true;
        exSprite.position.set(L.planet.pos.x, L.planet.pos.y);
        const target = L.planet.radius * 2 * WORM.explodedPlanetScale; // 140% of the diameter
        exSprite.scale.set(target / (tex ? tex.height : 256));
      } else {
        if (exSprite) exSprite.visible = false;
        this.planetField.sync([
          {
            id: "central",
            pos: L.planet.pos,
            radius: L.planet.radius,
            sprite: L.planet.sprite,
          },
        ]);
      }

      // Only ore-bearing bodies are synced → mined-out rocks disappear entirely.
      this.moonBodies.length = 0;
      for (const m of L.moons) {
        if (m.ore > 0) this.moonBodies.push(m);
        for (const s of m.subMoons) if (s.ore > 0) this.moonBodies.push(s);
      }
      this.moonField.sync(this.moonBodies);
      this.aliveAsteroids.length = 0;
      for (const a of L.asteroids) if (a.ore > 0) this.aliveAsteroids.push(a);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.asteroidField.sync(this.aliveAsteroids as any);
    }

    this.drawStation(state);
    this.droneField.sync(state.droneBay.units);
    this.repairField.sync(state.repairDrones);
    this.drawRepairBeams(state);
    this.readouts.sync(state.activeMinedBodies());
    this.drawNpcs(state);
    this.drawEnemies(state);
    this.drawCruisers(state);
    this.drawCarriers(state);
    this.drawWorm(state);
    this.drawMoonTurrets(state);
    this.drawPlayerFighters(state);
    this.drawFighterDrones(state);
    this.drawMissiles(state);
    this.drawFighterMissiles(state);
    this.drawRiftSwarm(state);
    this.drawWaypoint(state);
    this.drawServiceIcons(state);
    this.drawShipAndBeams(state);
    this.drawTurrets(state);
    this.drawCannons(state);
    this.drawBigCannons(state);
    this.floaters.update(dt);
    this.app.render();
  }

  /** Hull-mounted turrets: sprite at each active mount aiming at its target,
   * plus twin blue laser bolts and light-blue muzzle flashes. */
  private drawTurrets(state: GameState): void {
    const g = this.gTurretFx;
    g.clear();
    const tex = this.turretTex;
    const mounts = state.turretSystem.mounts;
    const active = state.layer === "planet" && state.purchasedUpgrades.has("mk2");
    // Turrets are a UNIVERSAL world size — they do NOT grow with the hull tier.
    const tScale = tex ? 12 / tex.height : 0.13;
    for (let i = 0; i < this.turretSprites.length; i++) {
      const sp = this.turretSprites[i]!;
      const m = mounts[i];
      if (!active || !m || !m.active) {
        sp.visible = false;
        continue;
      }
      sp.visible = true;
      sp.scale.set(tScale);
      sp.position.set(m.mx, m.my);
      sp.rotation = m.angle - Math.PI / 2; // barrels (texture +Y) point along aim
      if (m.flash > 0.01) {
        const dx = Math.cos(m.angle);
        const dy = Math.sin(m.angle);
        const px = -dy;
        const py = dx;
        for (const side of [1, -1]) {
          g.circle(
            m.mx + px * side * 5 + dx * 12,
            m.my + py * side * 5 + dy * 12,
            3 + m.flash * 3,
          ).fill({ color: 0x9fd8ff, alpha: 0.6 * m.flash });
        }
      }
    }
    // Blue laser bolts.
    for (const b of state.turretSystem.bullets) {
      const len = Math.hypot(b.vx, b.vy) || 1;
      const ux = b.vx / len;
      const uy = b.vy / len;
      const ax = b.x - ux * 6;
      const ay = b.y - uy * 6;
      const bx = b.x + ux * 6;
      const by = b.y + uy * 6;
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 3.5, color: 0x39c6ff, alpha: 0.4 });
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 1.3, color: 0xd6f2ff, alpha: 0.95 });
    }
  }

  /** Mk IV spinal cannons: 3 cannon sprites, big blue bolts, and the aim crosshair. */
  private drawCannons(state: GameState): void {
    const g = this.gCannonFx;
    g.clear();
    const tex = this.cannonTex;
    const active = state.layer === "planet" && state.cannonsUnlocked;
    // Engagement-area ring: the spinal cannons' reach (bolt travel). The Big
    // Cannons share it (their target range = the cannons' reach), so on the
    // Battleship this same ring marks where both weapon lines engage.
    if (active) {
      const mx = state.mothership.pos.x;
      const my = state.mothership.pos.y;
      const cannonR = CANNON.boltSpeed * CANNON.boltLife;
      g.circle(mx, my, cannonR).stroke({ width: 2, color: 0xffa24a, alpha: 0.16 });
      if (state.mkvPath === "battleship") {
        g.circle(mx, my, BIG_CANNON.targetRange).stroke({ width: 1.5, color: 0xff6a3a, alpha: 0.22 });
      }
    }
    const cScale = tex
      ? (MOTHERSHIP.spriteWorldHeight * 5.25 * 0.14) / tex.height
      : 0.1;
    const cannons = state.cannonSystem.cannons;
    for (let i = 0; i < this.cannonSprites.length; i++) {
      const sp = this.cannonSprites[i]!;
      const c = cannons[i];
      if (!active || !c || !c.active) {
        sp.visible = false;
        continue;
      }
      sp.visible = true;
      sp.scale.set(cScale);
      sp.position.set(c.mx, c.my);
      sp.rotation = c.angle - Math.PI / 2;
      if (c.flash > 0.01) {
        const dx = Math.cos(c.angle);
        const dy = Math.sin(c.angle);
        g.circle(c.mx + dx * 22, c.my + dy * 22, 6 + c.flash * 7).fill({
          color: 0x9fd8ff,
          alpha: 0.6 * c.flash,
        });
      }
    }
    // Big blue bolts.
    for (const b of state.cannonSystem.bullets) {
      const len = Math.hypot(b.vx, b.vy) || 1;
      const ux = b.vx / len;
      const uy = b.vy / len;
      g.moveTo(b.x - ux * 11, b.y - uy * 11)
        .lineTo(b.x + ux * 11, b.y + uy * 11)
        .stroke({ width: 9, color: 0x4fc3ff, alpha: 0.35 });
      g.moveTo(b.x - ux * 11, b.y - uy * 11)
        .lineTo(b.x + ux * 11, b.y + uy * 11)
        .stroke({ width: 4, color: 0xeaffff, alpha: 0.95 });
      g.circle(b.x, b.y, 4).fill({ color: 0xeaffff, alpha: 0.9 });
    }
    // Aim crosshair — tracks the resolved target (manual point, locked moon/enemy,
    // or the auto-combat target).
    const reticle = state.cannonReticle;
    if (active && reticle) {
      const x = reticle.x;
      const y = reticle.y;
      const col = reticle.locked ? 0xffd24a : 0xff5a5a; // gold when locked on a pick
      g.circle(x, y, 15).stroke({ width: 2, color: col, alpha: 0.85 });
      g.moveTo(x - 22, y)
        .lineTo(x - 7, y)
        .moveTo(x + 7, y)
        .lineTo(x + 22, y)
        .moveTo(x, y - 22)
        .lineTo(x, y - 7)
        .moveTo(x, y + 7)
        .lineTo(x, y + 22)
        .stroke({ width: 2, color: col, alpha: 0.85 });
    }
  }

  /** Mk V Battleship big cannons: 2 large gun sprites + twin piercing beams. */
  private drawBigCannons(state: GameState): void {
    const g = this.gBigCannonFx;
    g.clear();
    const tex = this.cannonTex;
    const active = state.layer === "planet" && state.bigCannonsUnlocked;
    const bScale = tex
      ? (MOTHERSHIP.spriteWorldHeight *
          5.25 *
          0.14 *
          (BIG_CANNON.spriteWorldHeight / CANNON.spriteWorldHeight)) /
        tex.height
      : 0.2;
    const guns = state.bigCannonSystem.guns;
    for (let i = 0; i < this.bigCannonSprites.length; i++) {
      const sp = this.bigCannonSprites[i]!;
      const c = guns[i];
      if (!active || !c || !c.active) {
        sp.visible = false;
        continue;
      }
      sp.visible = true;
      sp.scale.set(bScale);
      sp.position.set(c.mx, c.my);
      sp.rotation = c.angle - Math.PI / 2;
      if (c.flash > 0.01) {
        const dx = Math.cos(c.angle);
        const dy = Math.sin(c.angle);
        g.circle(c.mx + dx * 34, c.my + dy * 34, 9 + c.flash * 11).fill({
          color: 0xbfe6ff,
          alpha: 0.7 * c.flash,
        });
      }
    }
    // Twin "kamehameha" beams — growing white-hot lines with a blue halo; they
    // brighten as they extend and fade out over the last third of their life.
    const w = BIG_CANNON.beamWidth;
    for (const b of state.bigCannonSystem.beams) {
      const fade = b.t > 0.7 ? 1 - (b.t - 0.7) / 0.3 : 1; // fade over the final 30%
      g.moveTo(b.x0, b.y0)
        .lineTo(b.x1, b.y1)
        .stroke({ width: w * 3.4, color: 0x59d0ff, alpha: 0.22 * fade });
      g.moveTo(b.x0, b.y0)
        .lineTo(b.x1, b.y1)
        .stroke({ width: w * 1.8, color: 0x9fe6ff, alpha: 0.5 * fade });
      g.moveTo(b.x0, b.y0)
        .lineTo(b.x1, b.y1)
        .stroke({ width: w * 0.7, color: 0xffffff, alpha: 0.95 * fade });
      // Bright bloom at the muzzle and the leading tip.
      g.circle(b.x0, b.y0, w * 1.5).fill({ color: 0xeaffff, alpha: 0.85 * fade });
      g.circle(b.x1, b.y1, w * 1.7).fill({ color: 0xeaffff, alpha: 0.8 * fade });
    }
  }

  /** Green twin repair beams from each mechanical drone to the hull. */
  private drawRepairBeams(state: GameState): void {
    const g = this.gRepairFx;
    g.clear();
    for (const u of state.repairDrones) {
      const t = u.beamTarget;
      if (!t) continue;
      const dx = t.x - u.pos.x;
      const dy = t.y - u.pos.y;
      const len = Math.hypot(dx, dy) || 1;
      const px = -dy / len;
      const py = dx / len;
      for (const side of [1, -1]) {
        const ax = u.pos.x + px * side * 2.5;
        const ay = u.pos.y + py * side * 2.5;
        g.moveTo(ax, ay).lineTo(t.x, t.y).stroke({ width: 3, color: 0x2fe06a, alpha: 0.16 });
        g.moveTo(ax, ay).lineTo(t.x, t.y).stroke({ width: 1.1, color: 0xc8ffd8, alpha: 0.9 });
      }
      g.circle(t.x, t.y, 2).fill({ color: 0xc8ffd8, alpha: 0.55 });
    }
  }

  /** Render competitor NPC hulls: MTS_1 sprite, cyan mining beams (same as the
   * player), a shield bubble, and HP/shield mini-bars. */
  private drawNpcs(state: GameState): void {
    const ships = state.npcShips;
    const g = this.gNpc;
    g.clear();
    const scale =
      (this.baseShipScales[0] ?? 1) *
      0.82 *
      (state.layer === "system" ? 0.25 : 1);
    const F = MINING_LASER.gunForward;
    const S = MINING_LASER.gunSpread;
    for (let i = 0; i < this.npcSprites.length; i++) {
      const sp = this.npcSprites[i]!;
      const s = ships[i];
      if (!s || state.layer !== "planet") {
        sp.visible = false;
        continue;
      }
      sp.visible = true;
      sp.scale.set(scale);
      sp.position.set(s.pos.x, s.pos.y);
      sp.rotation = s.heading - Math.PI / 2;

      if (s.impact) {
        const fx = Math.cos(s.heading);
        const fy = Math.sin(s.heading);
        const px = -fy;
        const py = fx;
        const guns = [
          { x: s.pos.x + fx * F + px * S, y: s.pos.y + fy * F + py * S },
          { x: s.pos.x + fx * F - px * S, y: s.pos.y + fy * F - py * S },
        ];
        for (const gun of guns) {
          g.moveTo(gun.x, gun.y)
            .lineTo(s.impact.x, s.impact.y)
            .stroke({ width: 3.5, color: 0x39c6ff, alpha: 0.12 });
          g.moveTo(gun.x, gun.y)
            .lineTo(s.impact.x, s.impact.y)
            .stroke({ width: 1, color: 0xeaffff, alpha: 0.85 });
        }
        g.circle(s.impact.x, s.impact.y, 1.8).fill({ color: 0xffffff, alpha: 0.8 });
      }

      if (s.shield > 0) {
        g.circle(s.pos.x, s.pos.y, NPC.collisionRadius + 8).stroke({
          width: 1 + s.shieldFlash * 2,
          color: 0x6cc6ff,
          alpha: 0.12 + (s.shield / s.maxShield) * 0.2 + s.shieldFlash * 0.5,
        });
      }
      this.drawMiniBar(g, s.pos.x, s.pos.y - 34, s.shield / s.maxShield, 0x4fb8ff);
      this.drawMiniBar(g, s.pos.x, s.pos.y - 30, s.hp / s.maxHp, 0xff6a4a);
    }
  }

  /** Render hostile raiders: dark/red hull sprite, 1–2 red turrets that track the
   * player, red laser bolts, a red shield bubble, and HP/shield mini-bars. */
  private drawEnemies(state: GameState): void {
    const ships = state.enemyShips;
    const g = this.gEnemy;
    g.clear();
    this.gThrustNpc.clear(); // shared plume/contrail layer for all fleet + hostile hulls
    const eScale = this.enemyTex
      ? ENEMY.spriteWorldHeight / this.enemyTex.height
      : 0.42;
    const tScale = this.turretTex
      ? (ENEMY.spriteWorldHeight * 0.24) / this.turretTex.height
      : 0.08;
    const inPlanet = state.layer === "planet";
    for (let i = 0; i < this.enemySprites.length; i++) {
      const sp = this.enemySprites[i]!;
      const s = ships[i];
      const ta = this.enemyTurretSprites[i * 2]!;
      const tb = this.enemyTurretSprites[i * 2 + 1]!;
      if (!s || !inPlanet || s.hp <= 0) {
        sp.visible = false;
        ta.visible = false;
        tb.visible = false;
        continue;
      }
      sp.visible = true;
      sp.scale.set(eScale);
      sp.position.set(s.pos.x, s.pos.y);
      sp.rotation = s.heading - Math.PI / 2; // hull front (+Y) faces heading
      const espd = Math.hypot(s.vel.x, s.vel.y);
      this.drawThrusters(this.gThrustNpc, s.pos.x, s.pos.y, s.heading, espd, 210, sp.width, sp.height, 2, 0.66, i * 1.7);
      this.drawWingTrails(this.gThrustNpc, s.pos.x, s.pos.y, s.heading, espd, 210, sp.width, sp.height);

      const fx = Math.cos(s.heading);
      const fy = Math.sin(s.heading);
      const px = -fy;
      const py = fx;
      const tsp = [ta, tb];
      for (let j = 0; j < 2; j++) {
        const tspr = tsp[j]!;
        if (j >= s.turretCount) {
          tspr.visible = false;
          continue;
        }
        const off = ENEMY.mounts[j]!;
        const mx = s.pos.x + fx * off.fwd + px * off.lat;
        const my = s.pos.y + fy * off.fwd + py * off.lat;
        const tt = s.turrets[j]!;
        tspr.visible = true;
        tspr.scale.set(tScale);
        tspr.position.set(mx, my);
        tspr.rotation = tt.angle - Math.PI / 2;
        if (tt.flash > 0.01) {
          const dx = Math.cos(tt.angle);
          const dy = Math.sin(tt.angle);
          g.circle(mx + dx * 10, my + dy * 10, 3 + tt.flash * 3).fill({
            color: 0xff5a3a,
            alpha: 0.6 * tt.flash,
          });
        }
      }

      if (s.shield > 0) {
        g.circle(s.pos.x, s.pos.y, ENEMY.collisionRadius + 10).stroke({
          width: 1 + s.shieldFlash * 2,
          color: 0xff7a5a,
          alpha: 0.12 + (s.shield / s.maxShield) * 0.2 + s.shieldFlash * 0.5,
        });
      }
      this.drawMiniBar(g, s.pos.x, s.pos.y - 42, s.shield / s.maxShield, 0xffa06a);
      this.drawMiniBar(g, s.pos.x, s.pos.y - 38, s.hp / s.maxHp, 0xff3a2a);
    }

    // Red laser bolts.
    for (const b of state.enemyBullets) {
      const len = Math.hypot(b.vx, b.vy) || 1;
      const ux = b.vx / len;
      const uy = b.vy / len;
      const ax = b.x - ux * 7;
      const ay = b.y - uy * 7;
      const bx = b.x + ux * 7;
      const by = b.y + uy * 7;
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 4, color: 0xff2a18, alpha: 0.4 });
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 1.4, color: 0xffd0c4, alpha: 0.95 });
    }
  }

  /** Capital-ship cruisers: big hull, rotating side turrets, red bolts, HP bars. */
  private drawCruisers(state: GameState): void {
    const ships = state.cruiserShips;
    const g = this.gCruiser;
    g.clear();
    const eScale = this.cruiserTex
      ? CRUISER.spriteWorldHeight / this.cruiserTex.height
      : 0.5;
    // Turrets are the same on-screen size as the player's (12 world units tall).
    const tScale = this.turretTex ? 12 / this.turretTex.height : 0.13;
    const nMounts = CRUISER.mounts.length;
    const inPlanet = state.layer === "planet";
    for (let i = 0; i < this.cruiserSprites.length; i++) {
      const sp = this.cruiserSprites[i]!;
      const s = ships[i];
      if (!s || !inPlanet || s.hp <= 0) {
        sp.visible = false;
        for (let j = 0; j < nMounts; j++) {
          const tspr = this.cruiserTurretSprites[i * nMounts + j];
          if (tspr) tspr.visible = false;
        }
        continue;
      }
      sp.visible = true;
      sp.scale.set(eScale);
      sp.position.set(s.pos.x, s.pos.y);
      sp.rotation = s.heading - Math.PI / 2; // hull front (+Y) faces heading
      const cspd = Math.hypot(s.vel.x, s.vel.y);
      this.drawThrusters(this.gThrustNpc, s.pos.x, s.pos.y, s.heading, cspd, 160, sp.width, sp.height, 3, 0.6, i * 2.3);

      const h = s.heading;
      const ct = Math.sin(h); // cos(h − π/2), matching the hull sprite rotation
      const st = -Math.cos(h); // sin(h − π/2)
      const hs = CRUISER.spriteWorldHeight / CRUISER.spriteTexHeight; // mount scale (== the sim)
      for (let j = 0; j < nMounts; j++) {
        const tspr = this.cruiserTurretSprites[i * nMounts + j]!;
        if (j >= s.turretCount) {
          tspr.visible = false;
          continue;
        }
        const off = CRUISER.mounts[j]!;
        const mx = s.pos.x + hs * (off.ox * ct - off.oy * st);
        const my = s.pos.y + hs * (off.ox * st + off.oy * ct);
        const tt = s.turrets[j]!;
        tspr.visible = true;
        tspr.scale.set(tScale);
        tspr.position.set(mx, my);
        tspr.rotation = tt.angle - Math.PI / 2;
        if (tt.flash > 0.01) {
          const dx = Math.cos(tt.angle);
          const dy = Math.sin(tt.angle);
          g.circle(mx + dx * 13, my + dy * 13, 4 + tt.flash * 4).fill({
            color: 0xff5a3a,
            alpha: 0.6 * tt.flash,
          });
        }
      }

      if (s.shield > 0) {
        g.circle(s.pos.x, s.pos.y, CRUISER.collisionRadius + 12).stroke({
          width: 1 + s.shieldFlash * 2,
          color: 0xff7a5a,
          alpha: 0.12 + (s.shield / s.maxShield) * 0.2 + s.shieldFlash * 0.5,
        });
      }
      this.drawMiniBar(g, s.pos.x, s.pos.y - 60, s.shield / s.maxShield, 0xffa06a);
      this.drawMiniBar(g, s.pos.x, s.pos.y - 55, s.hp / s.maxHp, 0xff3a2a);

      // Calling for backup → broadcast expanding radio rings.
      if (s.backupState === 1) {
        const prog = 1 - s.backupTimer / CRUISER.backupRadioTime; // 0..1
        for (let k = 0; k < 3; k++) {
          const phase = (prog * 3 + k / 3) % 1;
          g.circle(s.pos.x, s.pos.y, 46 + phase * 380).stroke({
            width: 2.5,
            color: 0x7fe6ff,
            alpha: 0.5 * (1 - phase),
          });
        }
      }
    }

    // Red laser bolts (bigger than a fighter's).
    for (const b of state.cruiserBullets) {
      const len = Math.hypot(b.vx, b.vy) || 1;
      const ux = b.vx / len;
      const uy = b.vy / len;
      const ax = b.x - ux * 9;
      const ay = b.y - uy * 9;
      const bx = b.x + ux * 9;
      const by = b.y + uy * 9;
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 5, color: 0xff2a18, alpha: 0.4 });
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 1.8, color: 0xffd0c4, alpha: 0.95 });
    }
  }

  /** Capital-ship carriers: big evil hull, 8 free-tracking turrets, red bolts, HP
   * bars, and radio rings while calling backup. Mirrors drawCruisers. */
  private drawCarriers(state: GameState): void {
    const ships = state.carrierShips;
    const g = this.gCarrier;
    g.clear();
    const tex = this.carrierTex;
    const eScale = tex ? ENEMY_CARRIER.spriteWorldHeight / tex.height : 0.6;
    // Turrets are the same on-screen size as the player's (12 world units tall).
    const tScale = this.turretTex ? 12 / this.turretTex.height : 0.13;
    const nMounts = ENEMY_CARRIER.mounts.length;
    const inPlanet = state.layer === "planet";
    // Until the real enemy_carrier.png ships, we fall back to the cruiser texture —
    // tint that stand-in red so it still reads as a distinct, meaner ship.
    const placeholder = tex != null && tex === this.cruiserTex;
    for (let i = 0; i < this.carrierSprites.length; i++) {
      const sp = this.carrierSprites[i]!;
      const s = ships[i];
      if (!s || !inPlanet || s.hp <= 0) {
        sp.visible = false;
        for (let j = 0; j < nMounts; j++) {
          const tspr = this.carrierTurretSprites[i * nMounts + j];
          if (tspr) tspr.visible = false;
        }
        continue;
      }
      sp.visible = true;
      sp.tint = placeholder ? 0xff8a8a : 0xffffff;
      sp.scale.set(eScale);
      sp.position.set(s.pos.x, s.pos.y);
      sp.rotation = s.heading + Math.PI / 2; // FLIPPED nose-first (its nose art is at the top)
      const cspd = Math.hypot(s.vel.x, s.vel.y);
      this.drawThrusters(this.gThrustNpc, s.pos.x, s.pos.y, s.heading, cspd, 150, sp.width, sp.height, 3, 0.62, i * 1.7);

      const h = s.heading;
      const ct = -Math.sin(h); // cos(h + π/2) — carrier renders flipped (nose-first)
      const st = Math.cos(h); // sin(h + π/2)
      const hs = ENEMY_CARRIER.spriteWorldHeight / ENEMY_CARRIER.spriteTexHeight; // mount scale (== the sim)
      for (let j = 0; j < nMounts; j++) {
        const tspr = this.carrierTurretSprites[i * nMounts + j]!;
        const off = ENEMY_CARRIER.mounts[j]!;
        const mx = s.pos.x + hs * (off.ox * ct - off.oy * st);
        const my = s.pos.y + hs * (off.ox * st + off.oy * ct);
        const tt = s.turrets[j]!;
        tspr.visible = true;
        tspr.scale.set(tScale);
        tspr.position.set(mx, my);
        tspr.rotation = tt.angle - Math.PI / 2;
        if (tt.flash > 0.01) {
          const dx = Math.cos(tt.angle);
          const dy = Math.sin(tt.angle);
          g.circle(mx + dx * 13, my + dy * 13, 4 + tt.flash * 4).fill({
            color: 0xff5a3a,
            alpha: 0.6 * tt.flash,
          });
        }
      }

      if (s.shield > 0) {
        g.circle(s.pos.x, s.pos.y, ENEMY_CARRIER.collisionRadius + 14).stroke({
          width: 1 + s.shieldFlash * 2,
          color: 0xff6a4a,
          alpha: 0.12 + (s.shield / s.maxShield) * 0.2 + s.shieldFlash * 0.5,
        });
      }
      this.drawMiniBar(g, s.pos.x, s.pos.y - 80, s.shield / s.maxShield, 0xffa06a);
      this.drawMiniBar(g, s.pos.x, s.pos.y - 74, s.hp / s.maxHp, 0xff3a2a);

      // Calling for backup → broadcast expanding radio rings.
      if (s.backupState === 1) {
        const prog = 1 - s.backupTimer / ENEMY_CARRIER.backupRadioTime; // 0..1
        for (let k = 0; k < 3; k++) {
          const phase = (prog * 3 + k / 3) % 1;
          g.circle(s.pos.x, s.pos.y, 60 + phase * 440).stroke({
            width: 2.5,
            color: 0x7fe6ff,
            alpha: 0.5 * (1 - phase),
          });
        }
      }
    }

    // Red laser bolts.
    for (const b of state.carrierBullets) {
      const len = Math.hypot(b.vx, b.vy) || 1;
      const ux = b.vx / len;
      const uy = b.vy / len;
      const ax = b.x - ux * 9;
      const ay = b.y - uy * 9;
      const bx = b.x + ux * 9;
      const by = b.y + uy * 9;
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 5, color: 0xff2a18, alpha: 0.4 });
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 1.8, color: 0xffd0c4, alpha: 0.95 });
    }
  }

  /** Eater of Worlds boss: the segment chain (head + body + tail), its charged beam,
   * body-segment bolts, per-segment HP bars, and burning wreckage. */
  /** MK VII: render the player as a worm — head/body/tail sprites, one turret per segment,
   * the forward maw beam, and turret bolts. Self-hides when the player isn't a worm. */
  private drawPlayerWorm(state: GameState): void {
    const g = this.gPlayerWorm;
    g.clear();
    const head = this.playerWormHead;
    const tail = this.playerWormTail;
    if (head) head.visible = false;
    if (tail) tail.visible = false;
    for (const sp of this.playerWormBodySprites) sp.visible = false;
    for (const sp of this.playerWormTurretSprites) sp.visible = false;
    if (this.playerRiftEntry) this.playerRiftEntry.visible = false;
    if (this.playerRiftExit) this.playerRiftExit.visible = false;
    const w = state.playerWorm;
    if (!w || !state.isWorm) return;

    const mech = state.isMkviii; // MK VIII swaps in the mechanical worm sprites
    const headTex = mech ? this.wormHeadTex8 : this.wormHeadTex;
    const bodyTex = mech ? this.wormBodyTex8 : this.wormBodyTex;
    const tailTex = mech ? this.wormTailTex8 : this.wormTailTex;
    const headScale = headTex ? MKVII.headWorldHeight / headTex.height : 0.1;
    const bodyScale = bodyTex ? MKVII.bodyWorldHeight / bodyTex.height : 0.1;
    const tailScale = tailTex ? MKVII.tailWorldHeight / tailTex.height : 0.1;
    // The worm renders at TRUE world positions in BOTH layers, so the body trails exactly like
    // the planet layer (any position-scaling toward the head makes the body drift sluggishly).
    // In the system view only the SPRITE scale shrinks — but enough that segments still overlap
    // into one continuous body rather than drifting apart.
    const sysMul = state.layer === "system" ? 0.7 : 1;
    const tt = this.turretTex;
    let bodyIdx = 0;
    let turIdx = 0;
    for (const s of w.segments) {
      const dpx = s.pos.x;
      const dpy = s.pos.y;
      let sp: Sprite | undefined;
      let scale = bodyScale;
      if (s.kind === "head") {
        sp = head;
        scale = headScale;
        if (sp && headTex) sp.texture = headTex;
      } else if (s.kind === "tail") {
        sp = tail;
        scale = tailScale;
        if (sp && tailTex) sp.texture = tailTex;
      } else {
        sp = this.playerWormBodySprites[bodyIdx++];
        scale = bodyScale;
        if (sp && bodyTex) sp.texture = bodyTex;
      }
      if (sp) {
        sp.visible = !s.hidden; // hidden while inside the rift (Mk VIII transit)
        sp.scale.set(scale * sysMul);
        sp.position.set(dpx, dpy);
        // Sprite up (+Y) → toward the head; the tail flips 180° so its point trails.
        sp.rotation = s.heading - Math.PI / 2 + (s.kind === "tail" ? Math.PI : 0);
        sp.tint = 0xffffff;
      }
      // One turret rides the middle of every body + tail segment.
      if (s.turret && tt && turIdx < this.playerWormTurretSprites.length) {
        const tsp = this.playerWormTurretSprites[turIdx++]!;
        tsp.texture = tt;
        tsp.visible = !s.hidden;
        tsp.scale.set((MKVII.turretWorldSize / tt.height) * sysMul);
        tsp.position.set(dpx, dpy);
        tsp.rotation = s.turret.angle - Math.PI / 2;
        if (s.turret.flash > 0) {
          const bx = Math.cos(s.turret.angle);
          const by = Math.sin(s.turret.angle);
          g.circle(dpx + bx * 18, dpy + by * 18, 5 * s.turret.flash).fill({
            color: 0xfff2a0,
            alpha: 0.8 * s.turret.flash,
          });
        }
      }
    }
    // Charge telegraph: a pulsing orb builds at the maw before the beam fires.
    if (w.beam.phase === "charge" && w.segments.length > 0) {
      const hs = w.segments[0]!;
      const chx = Math.cos(hs.heading);
      const chy = Math.sin(hs.heading);
      const cxp = hs.pos.x + chx * (MKVII.headWorldHeight * 0.4);
      const cyp = hs.pos.y + chy * (MKVII.headWorldHeight * 0.4);
      const cf = w.beam.charge;
      const rad = 6 + cf * 22;
      g.circle(cxp, cyp, rad).fill({ color: 0x9a3cff, alpha: 0.22 + 0.3 * cf });
      g.circle(cxp, cyp, rad * 0.55).fill({ color: 0xd7aaff, alpha: 0.45 + 0.4 * cf });
      g.circle(cxp, cyp, rad * 0.25).fill({ color: 0xffffff, alpha: 0.85 });
    }
    // The maw beam — a big creeping lance, then a fan of smaller beams (purple energy).
    // It fades out as the volley ends.
    const fade = w.beam.fade;
    const drawBeam = (
      b: { x0: number; y0: number; x1: number; y1: number },
      w0: number,
      w1: number,
      w2: number,
    ) => {
      g.moveTo(b.x0, b.y0).lineTo(b.x1, b.y1).stroke({ width: w0, color: 0x9a3cff, alpha: 0.26 * fade });
      g.moveTo(b.x0, b.y0).lineTo(b.x1, b.y1).stroke({ width: w1, color: 0xc77bff, alpha: 0.6 * fade });
      g.moveTo(b.x0, b.y0).lineTo(b.x1, b.y1).stroke({ width: w2, color: 0xffffff, alpha: 0.95 * fade });
    };
    if (w.beam.main) {
      drawBeam(w.beam.main, 45, 21, 8); // +50% girth again
      g.circle(w.beam.main.x0, w.beam.main.y0, 24).fill({ color: 0xd7aaff, alpha: 0.5 * fade }); // maw bloom
    }
    for (const fb of w.beam.fan) drawBeam(fb, 10, 5, 1.6);
    // (Missiles use the shared salvo system — drawn red in drawMissiles.)
    // Turret bolts.
    for (const b of w.bolts) {
      const len = Math.hypot(b.vx, b.vy) || 1;
      const ux = b.vx / len;
      const uy = b.vy / len;
      g.moveTo(b.x - ux * 7, b.y - uy * 7)
        .lineTo(b.x + ux * 7, b.y + uy * 7)
        .stroke({ width: 3, color: 0xffe08a, alpha: 0.9 });
    }
    // ----- Mk VIII "Time & Space Rift": the sweep laser + the two portal sprites -----
    const rift = w.rift;
    if (rift) {
      // The sweep laser (sweep phase only): a bright violet lance from the maw.
      if (rift.sweepBeam) {
        const b = rift.sweepBeam;
        g.moveTo(b.x0, b.y0).lineTo(b.x1, b.y1).stroke({ width: 26, color: 0x8a4cff, alpha: 0.3 });
        g.moveTo(b.x0, b.y0).lineTo(b.x1, b.y1).stroke({ width: 12, color: 0xc9a2ff, alpha: 0.7 });
        g.moveTo(b.x0, b.y0).lineTo(b.x1, b.y1).stroke({ width: 4, color: 0xffffff, alpha: 0.95 });
      }
      const place = (sp: Sprite | undefined, end: typeof rift.entry, widthFrac: number) => {
        if (!sp || end.fade <= 0) return;
        const tex = end.open > 0.02 ? this.riftOpenedTex : this.riftClosedTex;
        if (!tex) return;
        sp.texture = tex;
        sp.visible = true;
        sp.position.set(end.cx, end.cy);
        sp.rotation = end.heading + Math.PI / 2; // slit runs perpendicular to travel
        const base = (RIFT.riftWorldWidth / tex.width) * sysMul;
        const openScale = 0.08 + 0.92 * end.open; // closed = thin slit → open = full eye
        sp.scale.set(base * widthFrac, base * openScale);
        sp.alpha = end.fade;
        sp.tint = 0xffffff;
      };
      // Entry rift grows left→right with the sweep; the exit rift is drawn full.
      place(this.playerRiftEntry, rift.entry, rift.phase === "sweep" ? rift.sweepFrac : 1);
      place(this.playerRiftExit, rift.exit, 1);
    }
    // ----- Mk VIII "Maelstrom Coil": the shield bubble + per-segment sweeping lasers -----
    const coil = w.coil;
    if (coil) {
      // Shield bubble (behind the lasers), gently pulsing and dimming as it soaks damage.
      if (coil.phase === "active" && coil.shieldHp > 0) {
        const frac = coil.shieldHp / COIL.shieldHp;
        const pulse = 0.85 + 0.15 * Math.sin(performance.now() / 120);
        g.circle(coil.cx, coil.cy, coil.shieldR).fill({
          color: 0x3aa0ff,
          alpha: (0.06 + 0.08 * frac) * pulse,
        });
        g.circle(coil.cx, coil.cy, coil.shieldR).stroke({
          width: 4,
          color: 0x9fe0ff,
          alpha: (0.35 + 0.45 * frac) * pulse,
        });
        g.circle(coil.cx, coil.cy, coil.shieldR - 6).stroke({
          width: 1.5,
          color: 0xffffff,
          alpha: 0.22 * frac,
        });
      }
      // Per-segment sweeping lasers — outer glow, core, muzzle flash + an impact bloom at the tip.
      for (const L of coil.lasers) {
        g.moveTo(L.x0, L.y0).lineTo(L.x1, L.y1).stroke({ width: 12, color: 0x7a3cff, alpha: 0.15 });
        g.moveTo(L.x0, L.y0).lineTo(L.x1, L.y1).stroke({ width: 6, color: 0xb98cff, alpha: 0.5 });
        g.moveTo(L.x0, L.y0).lineTo(L.x1, L.y1).stroke({ width: 2.4, color: 0xffffff, alpha: 0.95 });
        g.circle(L.x0, L.y0, 8).fill({ color: 0xe6d2ff, alpha: 0.7 }); // muzzle flash at the segment
        g.circle(L.x1, L.y1, 11).fill({ color: 0x9fd0ff, alpha: 0.26 }); // impact bloom at the tip
        g.circle(L.x1, L.y1, 4.5).fill({ color: 0xffffff, alpha: 0.6 });
      }
    }
  }

  private drawWorm(state: GameState): void {
    const g = this.gWorm;
    g.clear();
    // Hide the whole pool first.
    if (this.wormHeadSprite) this.wormHeadSprite.visible = false;
    if (this.wormTailSprite) this.wormTailSprite.visible = false;
    for (const sp of this.wormBodySprites) sp.visible = false;
    const segs = state.wormSegments;
    if (state.layer !== "planet" || segs.length === 0) return;

    const headScale = this.wormHeadTex ? WORM.headWorldHeight / this.wormHeadTex.height : 0.1;
    const bodyScale = this.wormBodyTex ? WORM.bodyWorldHeight / this.wormBodyTex.height : 0.1;
    const tailScale = this.wormTailTex ? WORM.tailWorldHeight / this.wormTailTex.height : 0.1;
    const now = performance.now() / 1000;

    let bodyIdx = 0;
    for (const s of segs) {
      let sp: Sprite | undefined;
      let scale = bodyScale;
      if (s.kind === "head") {
        sp = this.wormHeadSprite;
        scale = headScale;
      } else if (s.kind === "tail") {
        sp = this.wormTailSprite;
        scale = tailScale;
      } else {
        sp = this.wormBodySprites[bodyIdx++];
        scale = bodyScale;
      }
      if (!sp) continue;
      sp.visible = true;
      sp.scale.set(scale);
      sp.position.set(s.pos.x, s.pos.y);
      // Sprite up (+Y) → toward the head; the tail is flipped 180° so its point trails.
      sp.rotation = s.heading - Math.PI / 2 + (s.kind === "tail" ? Math.PI : 0);
      sp.tint = s.dead ? 0x6a5a55 : 0xffffff; // wreckage chars/darkens

      if (s.dead && s.fireSpots.length) {
        // Burning wreckage: 2-3 flickering fires at the segment's random spots.
        const fx = Math.cos(s.heading);
        const fy = Math.sin(s.heading);
        const px = -fy;
        const py = fx;
        for (let k = 0; k < s.fireSpots.length; k++) {
          const fs = s.fireSpots[k]!;
          const wx = s.pos.x + fx * fs.fwd + px * fs.lat;
          const wy = s.pos.y + fy * fs.fwd + py * fs.lat;
          const fl = 0.6 + 0.4 * Math.sin(now * 9 + k * 2.1 + s.pos.x * 0.05);
          g.circle(wx, wy, 6 + fl * 6).fill({ color: 0xff7a1e, alpha: 0.5 });
          g.circle(wx, wy, 3 + fl * 3).fill({ color: 0xffe08a, alpha: 0.85 });
        }
      } else if (s.hp < s.maxHp) {
        // Damaged (living) segment → HP bar (head sits higher).
        this.drawMiniBar(g, s.pos.x, s.pos.y - (s.kind === "head" ? 82 : 54), s.hp / s.maxHp, 0xff3a2a);
      }
    }

    // Head charged beam: telegraph glow while charging, then the firing beam.
    const head = segs.find((s) => s.kind === "head");
    if (head && !head.dead) {
      const dx = Math.cos(head.heading);
      const dy = Math.sin(head.heading);
      const mx = head.pos.x + dx * (WORM.headWorldHeight * 0.5);
      const my = head.pos.y + dy * (WORM.headWorldHeight * 0.5);
      if (state.wormBeamPhase === "charge") {
        const cf = state.wormCharge;
        // Telegraph the aim line so the player sees where the beam will fire.
        g.moveTo(mx, my)
          .lineTo(mx + dx * WORM.beamLength, my + dy * WORM.beamLength)
          .stroke({ width: 2 + cf * 5, color: 0xff3020, alpha: 0.18 + cf * 0.5 });
        // Growing charge core at the maw.
        const r = 6 + cf * 30;
        g.circle(mx, my, r).fill({ color: 0xff2a10, alpha: 0.35 + cf * 0.45 });
        g.circle(mx, my, r * 0.5).fill({ color: 0xffe070, alpha: 0.6 + cf * 0.35 });
      }
      const beam = state.wormBeam;
      if (beam && state.wormBeamPhase === "fire") {
        const flick = 0.8 + 0.2 * Math.sin(now * 40);
        g.moveTo(beam.x0, beam.y0)
          .lineTo(beam.x1, beam.y1)
          .stroke({ width: WORM.beamWidth * 2, color: 0x7a0a04, alpha: 0.25 });
        g.moveTo(beam.x0, beam.y0)
          .lineTo(beam.x1, beam.y1)
          .stroke({ width: WORM.beamWidth, color: 0xff2a12, alpha: 0.5 * flick });
        g.moveTo(beam.x0, beam.y0)
          .lineTo(beam.x1, beam.y1)
          .stroke({ width: WORM.beamWidth * 0.4, color: 0xffe6b0, alpha: 0.95 });
        g.circle(mx, my, WORM.beamWidth * 0.9).fill({ color: 0xffd070, alpha: 0.7 });
      }
    }

    // Body-segment red bolts.
    for (const b of state.wormBullets) {
      const len = Math.hypot(b.vx, b.vy) || 1;
      const ux = b.vx / len;
      const uy = b.vy / len;
      const ax = b.x - ux * 8;
      const ay = b.y - uy * 8;
      const bx = b.x + ux * 8;
      const by = b.y + uy * 8;
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 5, color: 0xff2a18, alpha: 0.4 });
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 1.8, color: 0xffd0c4, alpha: 0.95 });
    }
  }

  /** Rogue-planet moon turrets: a red hostile auto-cannon centred on each armed
   * moon (rotating to aim), plus their red bolts. */
  private drawMoonTurrets(state: GameState): void {
    const g = this.gMoonTurret;
    g.clear();
    if (state.layer !== "planet") return;
    for (const t of state.moonTurrets) {
      if (t.dead || t.hp <= 0) continue;
      const r = t.radius;
      g.circle(t.pos.x, t.pos.y, r)
        .fill({ color: 0x2b2230 })
        .stroke({ width: 2, color: 0x120a10 });
      g.circle(t.pos.x, t.pos.y, r * 0.62).fill({ color: 0x3a2732 });
      const bx = Math.cos(t.angle);
      const by = Math.sin(t.angle);
      const px = -by;
      const py = bx;
      const bl = r * 1.7;
      const bw = Math.max(1.6, r * 0.3);
      const tx = t.pos.x + bx * bl;
      const ty = t.pos.y + by * bl;
      g.poly([
        t.pos.x + px * bw,
        t.pos.y + py * bw,
        t.pos.x - px * bw,
        t.pos.y - py * bw,
        tx - px * bw * 0.7,
        ty - py * bw * 0.7,
        tx + px * bw * 0.7,
        ty + py * bw * 0.7,
      ])
        .fill({ color: 0x6a5560 })
        .stroke({ width: 1.5, color: 0x120a10 });
      g.circle(t.pos.x, t.pos.y, r * 0.34).fill({ color: 0xff3b30, alpha: 0.92 });
      if (t.flash > 0.01) {
        g.circle(tx, ty, 4 + t.flash * 5).fill({ color: 0xff5a3a, alpha: 0.7 * t.flash });
      }
      if (t.hp < t.maxHp) {
        this.drawMiniBar(g, t.pos.x, t.pos.y - r - 8, t.hp / t.maxHp, 0xff3a2a);
      }
    }
    for (const b of state.moonTurretBullets) {
      const len = Math.hypot(b.vx, b.vy) || 1;
      const ux = b.vx / len;
      const uy = b.vy / len;
      const ax = b.x - ux * 8;
      const ay = b.y - uy * 8;
      const cx = b.x + ux * 8;
      const cy = b.y + uy * 8;
      g.moveTo(ax, ay).lineTo(cx, cy).stroke({ width: 4.5, color: 0xff2a18, alpha: 0.4 });
      g.moveTo(ax, ay).lineTo(cx, cy).stroke({ width: 1.6, color: 0xffd0c4, alpha: 0.95 });
    }
  }

  /** Player escort fighters: blue hull, cyan bolts, friendly HP bars. */
  private drawPlayerFighters(state: GameState): void {
    const ships = state.playerFighterShips;
    const g = this.gPFighter;
    g.clear();
    const scale = this.pFighterTex
      ? PLAYER_FIGHTER.spriteWorldHeight / this.pFighterTex.height
      : 0.42;
    const inPlanet = state.layer === "planet";
    for (let i = 0; i < this.pFighterSprites.length; i++) {
      const sp = this.pFighterSprites[i]!;
      const s = ships[i];
      if (!s || !inPlanet || s.hp <= 0) {
        sp.visible = false;
        continue;
      }
      sp.visible = true;
      sp.scale.set(scale);
      sp.position.set(s.pos.x, s.pos.y);
      sp.rotation = s.heading - Math.PI / 2;
      const pspd = Math.hypot(s.vel.x, s.vel.y);
      this.drawThrusters(this.gThrustNpc, s.pos.x, s.pos.y, s.heading, pspd, 210, sp.width, sp.height, 2, 0.66, i * 1.3 + 40);
      this.drawWingTrails(this.gThrustNpc, s.pos.x, s.pos.y, s.heading, pspd, 210, sp.width, sp.height);
      for (const t of s.turrets) {
        if (t.flash > 0.01) {
          const dx = Math.cos(t.angle);
          const dy = Math.sin(t.angle);
          g.circle(s.pos.x + dx * 12, s.pos.y + dy * 12, 3 + t.flash * 3).fill({
            color: 0x8fdcff,
            alpha: 0.6 * t.flash,
          });
        }
      }
      if (s.shield > 0) {
        g.circle(s.pos.x, s.pos.y, PLAYER_FIGHTER.collisionRadius + 8).stroke({
          width: 1 + s.shieldFlash * 2,
          color: 0x6ec8ff,
          alpha: 0.12 + (s.shield / s.maxShield) * 0.2 + s.shieldFlash * 0.5,
        });
      }
      this.drawMiniBar(g, s.pos.x, s.pos.y - 34, s.shield / s.maxShield, 0x7fd0ff);
      this.drawMiniBar(g, s.pos.x, s.pos.y - 30, s.hp / s.maxHp, 0x5fe08a);
    }
    for (const b of state.playerFighterBullets) {
      const len = Math.hypot(b.vx, b.vy) || 1;
      const ux = b.vx / len;
      const uy = b.vy / len;
      const ax = b.x - ux * 7;
      const ay = b.y - uy * 7;
      const bx = b.x + ux * 7;
      const by = b.y + uy * 7;
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 4, color: 0x2ad2ff, alpha: 0.4 });
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 1.4, color: 0xdffaff, alpha: 0.95 });
    }
  }

  /** Swarm-lord homing missiles: a bright head with an orange exhaust ribbon. */
  private drawMissiles(state: GameState): void {
    const g = this.gMissile;
    g.clear();
    if (state.layer !== "planet") return;
    // The Mk VII worm fires the same salvo tinted RED; every other hull is icy blue.
    const worm = state.isWorm;
    const purple = state.isMkviiSwl; // Mk VII SWL fires a light-purple salvo
    const trailCol = worm ? 0xff6a4a : purple ? 0xb98cff : 0x49a6ff;
    const plumeCol = worm ? 0xff3a1e : purple ? 0x9a6cff : 0x2f7cff;
    const headCol = worm ? 0xffe0d0 : purple ? 0xf0e6ff : 0xdfefff;
    for (const mi of state.playerMissiles) {
      const tr = mi.trail;
      for (let i = 1; i < tr.length; i++) {
        const a = i / tr.length;
        g.moveTo(tr[i - 1]!.x, tr[i - 1]!.y)
          .lineTo(tr[i]!.x, tr[i]!.y)
          .stroke({ width: 1 + 3 * a, color: trailCol, alpha: 0.5 * a }); // exhaust ribbon
      }
      const dx = Math.cos(mi.heading);
      const dy = Math.sin(mi.heading);
      g.circle(mi.pos.x - dx * 4, mi.pos.y - dy * 4, 3).fill({ color: plumeCol, alpha: 0.85 }); // plume
      g.circle(mi.pos.x, mi.pos.y, 3.5).fill({ color: headCol, alpha: 0.95 }); // head
    }
  }

  /** Mk VI SWL: escort-fighter + fighter-drone homing missiles — small icy-blue darts. */
  private drawFighterMissiles(state: GameState): void {
    const g = this.gFighterMissile;
    g.clear();
    if (state.layer !== "planet") return;
    const purple = state.isMkviiSwl; // Mk VII SWL: light-purple missiles
    const exCol = purple ? 0xc9a6ff : 0x6fe3ff;
    const plCol = purple ? 0x9a6cff : 0x2f7cff;
    const hdCol = purple ? 0xf2e9ff : 0xeafcff;
    for (const arr of [state.playerFighterMissiles, state.playerFighterDroneMissiles]) {
      for (const m of arr) {
        const dx = Math.cos(m.heading);
        const dy = Math.sin(m.heading);
        g.moveTo(m.pos.x - dx * 11, m.pos.y - dy * 11)
          .lineTo(m.pos.x, m.pos.y)
          .stroke({ width: 3, color: exCol, alpha: 0.8 }); // exhaust
        g.circle(m.pos.x - dx * 4, m.pos.y - dy * 4, 2.4).fill({ color: plCol, alpha: 0.8 }); // plume
        g.circle(m.pos.x, m.pos.y, 2.8).fill({ color: hdCol, alpha: 0.95 }); // head
      }
    }
  }

  /** Mk VII SWL Rift Swarm: the summoned allied NPCs (purple arrowheads, fighters bigger than
   * drones), their bolts, and the two purple rift portals they pour out of. */
  private drawRiftSwarm(state: GameState): void {
    const g = this.gRiftSwarm;
    g.clear();
    // Rift portals — the EOW rift sprite at each mouth: closed, then snaps open, then fades out.
    const plife = MKVI.swl7Rift.portalLife;
    for (let i = 0; i < this.riftPortalSprites.length; i++) {
      const sp = this.riftPortalSprites[i]!;
      const p = state.layer === "planet" ? state.riftPortals[i] : undefined;
      if (!p) {
        sp.visible = false;
        continue;
      }
      const opened = p.age > 0.28;
      const tex = opened ? this.riftOpenedTex : this.riftClosedTex;
      if (tex) sp.texture = tex;
      const grow = Math.min(1, p.age / 0.28);
      const dia = opened ? 215 : 156 * (0.4 + 0.6 * grow); // world diameter (+30% bigger rift)
      sp.visible = true;
      sp.position.set(p.x, p.y);
      sp.scale.set(dia / (sp.texture?.width || 1));
      sp.rotation = 0; // the rift hangs still — no spin
      sp.tint = 0xc9a6ff; // purple
      sp.alpha = p.age > plife - 1 ? Math.max(0, plife - p.age) : 1; // hold ~5s, then fade over the last 1s
    }
    if (state.layer !== "planet") return;
    // Arrows — slim purple needles trailing a long fading tail.
    for (const ar of state.riftArrows) {
      const tr = ar.trail;
      for (let i = 1; i < tr.length; i++) {
        const a = i / tr.length;
        g.moveTo(tr[i - 1]!.x, tr[i - 1]!.y)
          .lineTo(tr[i]!.x, tr[i]!.y)
          .stroke({ width: 0.6 + 3 * a, color: 0xb98cff, alpha: 0.55 * a });
      }
      const c = Math.cos(ar.heading);
      const sn = Math.sin(ar.heading);
      const nx = ar.pos.x;
      const ny = ar.pos.y;
      const len = 22;
      const wid = 2.6; // narrow + slim
      const px = -sn;
      const py = c;
      g.poly([
        nx + c * len, ny + sn * len, // sharp tip
        nx - c * len * 0.55 + px * wid, ny - sn * len * 0.55 + py * wid,
        nx - c * len * 0.32, ny - sn * len * 0.32, // tail notch
        nx - c * len * 0.55 - px * wid, ny - sn * len * 0.55 - py * wid,
      ]).fill({ color: 0xd9b8ff, alpha: 0.98 });
      g.circle(nx + c * len, ny + sn * len, 1.8).fill({ color: 0xffffff, alpha: 0.95 }); // bright tip
    }
  }

  /** Click-to-move: a path line from the ship to its moveTarget + a pulsing destination marker.
   * World-space; works on both layers. The moveTarget clears itself once the ship arrives. */
  private drawWaypoint(state: GameState): void {
    const g = this.gWaypoint;
    g.clear();
    const wp = state.mothership.moveTarget;
    if (!wp) return;
    const sx = state.mothership.pos.x;
    const sy = state.mothership.pos.y;
    g.moveTo(sx, sy).lineTo(wp.x, wp.y).stroke({ width: 3, color: 0x5fd0ff, alpha: 0.4 });
    const t = (performance.now() % 1400) / 1400; // 0→1 pulse
    const R = 34;
    g.circle(wp.x, wp.y, R + 46 * t).stroke({ width: 2.5, color: 0x5fd0ff, alpha: 0.5 * (1 - t) });
    g.circle(wp.x, wp.y, R).stroke({ width: 3, color: 0x9fe6ff, alpha: 0.85 });
    g.circle(wp.x, wp.y, 5).fill({ color: 0xeafcff, alpha: 0.95 });
    for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as const) {
      g.moveTo(wp.x + dx * (R + 6), wp.y + dy * (R + 6))
        .lineTo(wp.x + dx * (R + 22), wp.y + dy * (R + 22))
        .stroke({ width: 3, color: 0x9fe6ff, alpha: 0.85 });
    }
  }

  /** Boss ring station (admin size-test): two counter-rotating rings from ONE sprite,
   * centered on the planet. Outer ≈ Ø1040 world units spinning clockwise, inner ≈ Ø600
   * spinning counter-clockwise. Uniform scale (width → ring diameter). No weapons yet. */
  private drawStation(state: GameState): void {
    const st = state.station;
    const g = this.gStation;
    g.clear();
    const texW = this.stationOuter.texture?.width ?? 0;
    const on = !!st && state.layer === "planet" && texW > 2;
    this.stationOuter.visible = on;
    this.stationInner.visible = on;
    if (!on || !st) {
      for (const s of this.stationWeaponSprites) s.visible = false;
      return;
    }
    const c = st.center;
    const outer = st.rings[0]!;
    const inner = st.rings[1]!;
    // Ring sprites ride the manager's rotation; a dead ring stops + darkens.
    this.stationOuter.position.set(c.x, c.y);
    this.stationOuter.scale.set(STATION.outerScale);
    this.stationOuter.rotation = outer.angle;
    this.stationOuter.tint = outer.dead ? 0x4a4744 : 0xffffff;
    this.stationInner.position.set(c.x, c.y);
    this.stationInner.scale.set(STATION.innerScale);
    this.stationInner.rotation = inner.angle;
    this.stationInner.tint = inner.dead ? 0x4a4744 : 0xffffff;
    const tt = this.turretTex;
    const ct = this.cannonTex;
    let si = 0;
    for (const ring of st.rings) {
      for (const w of ring.weapons) {
        this.drawStationWeaponFx(g, w, c);
        // Body: a pooled turret/cannon sprite (same textures the player uses).
        if (w.dead || w.kind === "hangar") continue;
        const tex = w.kind === "turret" ? tt : ct;
        if (!tex || si >= this.stationWeaponSprites.length) continue;
        const sp = this.stationWeaponSprites[si++]!;
        const wsize =
          w.kind === "turret"
            ? STATION.turretWorldSize
            : w.kind === "cannon"
              ? STATION.cannonWorldSize
              : STATION.bigCannonWorldSize;
        sp.texture = tex;
        sp.visible = true;
        sp.anchor.set(0.5, w.kind === "turret" ? 0.42 : 0.35);
        sp.scale.set(wsize / tex.height);
        sp.position.set(w.pos.x, w.pos.y);
        const aim = w.angle !== 0 ? w.angle : Math.atan2(w.pos.y - c.y, w.pos.x - c.x);
        sp.rotation = aim - Math.PI / 2; // textures point UP; aim rotates them
      }
    }
    for (let i = si; i < this.stationWeaponSprites.length; i++)
      this.stationWeaponSprites[i]!.visible = false;
    // Red turret/cannon bolts streaking at the player.
    for (const b of st.bolts) {
      const len = Math.hypot(b.vx, b.vy) || 1;
      const ux = b.vx / len;
      const uy = b.vy / len;
      const w = b.big ? 3 : 2;
      g.moveTo(b.x - ux * 8, b.y - uy * 8)
        .lineTo(b.x + ux * 8, b.y + uy * 8)
        .stroke({ width: w * 2, color: 0xff3a2a, alpha: 0.4 });
      g.moveTo(b.x - ux * 8, b.y - uy * 8)
        .lineTo(b.x + ux * 8, b.y + uy * 8)
        .stroke({ width: w, color: 0xffd0c4, alpha: 0.95 });
    }
    // Big-cannon red beams.
    for (const bm of st.beams) {
      const fade = bm.t > 0.7 ? 1 - (bm.t - 0.7) / 0.3 : 1;
      g.moveTo(bm.x0, bm.y0).lineTo(bm.x1, bm.y1).stroke({ width: 11, color: 0xff3020, alpha: 0.24 * fade });
      g.moveTo(bm.x0, bm.y0).lineTo(bm.x1, bm.y1).stroke({ width: 4.5, color: 0xff8060, alpha: 0.6 * fade });
      g.moveTo(bm.x0, bm.y0).lineTo(bm.x1, bm.y1).stroke({ width: 1.6, color: 0xffe0d0, alpha: 0.95 * fade });
    }
    // A dead ring smoulders — scatter flickering fire blobs around its band.
    for (const ring of st.rings) {
      if (!ring.dead) continue;
      const rr = ring.scale * 430; // ring-band radius in world units (texture band ≈ 430px)
      const n = 16;
      for (let i = 0; i < n; i++) {
        const a = ring.angle * 0.3 + (i / n) * Math.PI * 2;
        const fl = 0.5 + 0.5 * Math.sin(ring.burnT * 6 + i * 1.7);
        const rad = 6 + fl * 8;
        const fx = c.x + Math.cos(a) * rr;
        const fy = c.y + Math.sin(a) * rr;
        g.circle(fx, fy, rad).fill({ color: 0xff7326, alpha: 0.16 + 0.2 * fl });
        g.circle(fx, fy, rad * 0.5).fill({ color: 0xffd23a, alpha: 0.3 + 0.3 * fl });
      }
    }
  }

  /** Shields / hangar glow / wreck fire + muzzle flash for a station weapon. The body
   * itself is a pooled turret/cannon sprite (see drawStation). */
  private drawStationWeaponFx(g: Graphics, w: StationWeapon, c: { x: number; y: number }): void {
    const x = w.pos.x;
    const y = w.pos.y;
    if (w.dead) {
      g.circle(x, y, w.radius).fill({ color: 0x241f1d, alpha: 0.95 });
      const f = 0.5 + 0.5 * Math.sin(w.burnT * 9 + x * 0.05);
      g.circle(x, y, w.radius * 0.75).fill({ color: 0xff5a1e, alpha: 0.35 + 0.3 * f });
      g.circle(x - 2, y - 3, w.radius * 0.4).fill({ color: 0xffd24a, alpha: 0.5 * f });
      return;
    }
    if (w.kind === "hangar") {
      g.circle(x, y, w.radius * 0.7).fill({ color: 0xff4020, alpha: 0.55 });
      g.circle(x, y, w.radius).stroke({ width: 1.5, color: 0xff6030, alpha: 0.4 });
      return;
    }
    if (w.maxShield > 0 && w.shield > 0) {
      g.circle(x, y, w.radius + 5).stroke({
        width: 1.4 + w.shieldFlash * 2,
        color: 0xff6a6a,
        alpha: 0.2 + (w.shield / w.maxShield) * 0.4 + w.shieldFlash * 0.4,
      });
    }
    if (w.flash > 0.01) {
      const aim = w.angle !== 0 ? w.angle : Math.atan2(y - c.y, x - c.x);
      const bl = w.radius * 1.6;
      g.circle(x + Math.cos(aim) * bl, y + Math.sin(aim) * bl, 3 + w.flash * 5).fill({
        color: 0xffcaa0,
        alpha: 0.75 * w.flash,
      });
    }
  }

  /** Swarm-lord fighter drones — same hull as escort fighters, rendered 40% smaller. */
  private drawFighterDrones(state: GameState): void {
    const ships = state.playerFighterDroneShips;
    const g = this.gPDrone;
    g.clear();
    const base = this.pFighterTex
      ? PLAYER_FIGHTER.spriteWorldHeight / this.pFighterTex.height
      : 0.42;
    const scale = base * FIGHTER_DRONE.sizeMul;
    const cr = PLAYER_FIGHTER.collisionRadius * FIGHTER_DRONE.sizeMul;
    const inPlanet = state.layer === "planet";
    for (let i = 0; i < this.pDroneSprites.length; i++) {
      const sp = this.pDroneSprites[i]!;
      const s = ships[i];
      if (!s || !inPlanet || s.hp <= 0) {
        sp.visible = false;
        continue;
      }
      sp.visible = true;
      sp.scale.set(scale);
      sp.position.set(s.pos.x, s.pos.y);
      sp.rotation = s.heading - Math.PI / 2;
      const pspd = Math.hypot(s.vel.x, s.vel.y);
      this.drawThrusters(this.gThrustNpc, s.pos.x, s.pos.y, s.heading, pspd, 210, sp.width, sp.height, 2, 0.5, i * 1.1 + 90);
      for (const t of s.turrets) {
        if (t.flash > 0.01) {
          const dx = Math.cos(t.angle);
          const dy = Math.sin(t.angle);
          g.circle(s.pos.x + dx * 8, s.pos.y + dy * 8, 2 + t.flash * 2).fill({
            color: 0x8fdcff,
            alpha: 0.6 * t.flash,
          });
        }
      }
      if (s.shield > 0) {
        g.circle(s.pos.x, s.pos.y, cr + 5).stroke({
          width: 1 + s.shieldFlash * 1.5,
          color: 0x6ec8ff,
          alpha: 0.12 + (s.shield / s.maxShield) * 0.2 + s.shieldFlash * 0.4,
        });
      }
      this.drawMiniBar(g, s.pos.x, s.pos.y - 22, s.hp / s.maxHp, 0x5fe08a);
    }
    for (const b of state.playerFighterDroneBullets) {
      const len = Math.hypot(b.vx, b.vy) || 1;
      const ux = b.vx / len;
      const uy = b.vy / len;
      g.moveTo(b.x - ux * 5, b.y - uy * 5).lineTo(b.x + ux * 5, b.y + uy * 5).stroke({ width: 3, color: 0x2ad2ff, alpha: 0.4 });
      g.moveTo(b.x - ux * 5, b.y - uy * 5).lineTo(b.x + ux * 5, b.y + uy * 5).stroke({ width: 1.1, color: 0xdffaff, alpha: 0.95 });
    }
  }

  // ---- Engine thrusters + wing contrails (additive glow VFX) ----------------
  /** One glowing exhaust plume from a nozzle at (nx,ny) firing along (bx,by). */
  private drawPlume(
    g: Graphics,
    nx: number,
    ny: number,
    bx: number,
    by: number,
    W: number,
    L: number,
    intensity: number,
    purple = false, // Mk VII SWL: purple plumes instead of blue
  ): void {
    const ppx = -by; // perpendicular to the thrust axis
    const ppy = bx;
    const tri = (w: number, len: number, color: number, alpha: number) => {
      const tx = nx + bx * len;
      const ty = ny + by * len;
      g.poly([nx + ppx * w, ny + ppy * w, nx - ppx * w, ny - ppy * w, tx, ty]).fill({
        color,
        alpha,
      });
    };
    const glow = purple ? 0x6a1cff : 0x1c6bff;
    const mid = purple ? 0x9a4cff : 0x37a4ff;
    const inner = purple ? 0xd6b0ff : 0xa6e0ff;
    const fl1 = purple ? 0x7a2aff : 0x2a86ff;
    const fl2 = purple ? 0xe6cfff : 0xbfe6ff;
    tri(W * 1.85, L * 1.12, glow, 0.1 + 0.1 * intensity); // outer glow
    tri(W * 1.02, L, mid, 0.18 + 0.16 * intensity); // mid body
    tri(W * 0.5, L * 0.82, inner, 0.4 + 0.2 * intensity); // hot inner
    tri(W * 0.22, L * 0.6, 0xffffff, 0.65); // white core
    // Soft round nozzle flare (no hard glint lines).
    g.circle(nx, ny, W * 1.5).fill({ color: fl1, alpha: 0.14 + 0.1 * intensity });
    g.circle(nx, ny, W * 0.78).fill({ color: fl2, alpha: 0.45 });
    g.circle(nx, ny, W * 0.36).fill({ color: 0xffffff, alpha: 0.9 });
  }

  /** A row of `count` back-mounted thrusters mapped to a hull's rear edge. */
  private drawThrusters(
    g: Graphics,
    cx: number,
    cy: number,
    heading: number,
    speed: number,
    refSpeed: number,
    spriteW: number,
    spriteH: number,
    count: number,
    backFrac: number,
    seed: number,
    intensityOverride = -1,
    sizeMul = 1, // shrink the plumes without moving the nozzles (Mk V = 0.5)
    nozzles?: { lat: number; back: number }[], // custom nozzle layout (frac of halfW/halfH)
    purple = false, // Mk VII SWL: purple plumes
  ): void {
    if (count <= 0) return;
    const fx = Math.cos(heading);
    const fy = Math.sin(heading);
    const bx = -fx; // backward (opposite the nose)
    const by = -fy;
    const px = -fy; // perpendicular (matches hull mount math)
    const py = fx;
    const halfH = spriteH * 0.5;
    const halfW = spriteW * 0.5;
    const bd = halfH * backFrac;
    const intensity =
      intensityOverride >= 0
        ? intensityOverride
        : Math.max(0.25, Math.min(1, speed / refSpeed));
    if (intensity <= 0.02) return; // idle → no plume at all
    // Slow, gentle pulse only — no per-frame random (that read as fast blinking).
    const flick = 0.92 + 0.08 * Math.sin(this.lastRenderMs * 0.006 + seed);
    const W = Math.max(2.5, halfW * 0.32) * sizeMul;
    const L = W * (3.0 + intensity * 3.2) * flick;
    if (nozzles) {
      // Explicit nozzle layout (e.g. Swarm-lord: 1 centre + 2 behind each wing).
      for (const n of nozzles) {
        const nx = cx + bx * (halfH * n.back) + px * (n.lat * halfW);
        const ny = cy + by * (halfH * n.back) + py * (n.lat * halfW);
        this.drawPlume(g, nx, ny, bx, by, W, L, intensity, purple);
      }
      return;
    }
    const span = halfW * 0.52;
    for (let k = 0; k < count; k++) {
      const t = count === 1 ? 0 : (k / (count - 1)) * 2 - 1; // -1..+1
      const lat = t * span;
      const nx = cx + bx * bd + px * lat;
      const ny = cy + by * bd + py * lat;
      this.drawPlume(g, nx, ny, bx, by, W, L, intensity);
    }
  }

  /** Central Hub: pick the hull texture for a remote pilot's tier (worms fall back to Mk VI). */
  private remoteTexFor(tier: number): Texture | undefined {
    if (tier >= 5) return this.shipTexVIBts ?? this.shipTextures[3];
    if (tier >= 4) return this.shipTexVBts ?? this.shipTextures[3];
    return this.shipTextures[Math.max(0, Math.min(tier, 3))];
  }

  /** Central Hub: draw the OTHER pilots at their smoothed positions — a single hull sprite for
   * normal tiers, or a full head+body+tail worm chain for MK VII/VIII pilots (so they're seen as
   * the Eater-of-Worlds worm, not a Mk VI hull). Hidden when not in hub. Names live in the HUD. */
  private drawRemotePlayers(state: GameState): void {
    this.gRemoteThrust.clear();
    const players = state.hub ? [...state.remotePlayers.values()] : [];
    const sysMul = state.layer === "system" ? 0.7 : 1; // match drawPlayerWorm's system-view shrink
    let shipIdx = 0;
    let headIdx = 0;
    let bodyIdx = 0;
    let tailIdx = 0;
    for (const p of players) {
      if (p.tier >= 6 && p.wormTrail && p.wormTrail.length > 1) {
        // ----- Worm pilot (MK VII/VIII): draw the chain along the trailed segments -----
        const mech = p.tier >= 7; // MK VIII → mechanical worm art (falls back to the EOW art)
        const headTex = (mech ? this.wormHeadTex8 : this.wormHeadTex) ?? this.wormHeadTex;
        const bodyTex = (mech ? this.wormBodyTex8 : this.wormBodyTex) ?? this.wormBodyTex;
        const tailTex = (mech ? this.wormTailTex8 : this.wormTailTex) ?? this.wormTailTex;
        if (!headTex || !bodyTex || !tailTex) continue;
        const headScale = (MKVII.headWorldHeight / headTex.height) * sysMul;
        const bodyScale = (MKVII.bodyWorldHeight / bodyTex.height) * sysMul;
        const tailScale = (MKVII.tailWorldHeight / tailTex.height) * sysMul;
        const trail = p.wormTrail;
        const last = trail.length - 1;
        for (let k = 0; k < trail.length; k++) {
          const seg = trail[k]!;
          let sp: Sprite | undefined;
          let flip = 0;
          if (k === 0) {
            sp = this.remoteWormHeads[headIdx++];
            if (sp) {
              sp.texture = headTex;
              sp.scale.set(headScale);
            }
          } else if (k === last) {
            sp = this.remoteWormTails[tailIdx++];
            if (sp) {
              sp.texture = tailTex;
              sp.scale.set(tailScale);
              flip = Math.PI;
            }
          } else {
            sp = this.remoteWormBodies[bodyIdx++];
            if (sp) {
              sp.texture = bodyTex;
              sp.scale.set(bodyScale);
            }
          }
          if (!sp) continue; // pool exhausted (extremely unlikely) → skip this segment
          sp.visible = true;
          sp.position.set(seg.x, seg.y);
          sp.rotation = seg.heading - Math.PI / 2 + flip; // tail flips 180° so its point trails
        }
        const spd = Math.hypot(p.vx, p.vy);
        if (spd > 8) {
          const hh = trail[0]!;
          this.drawThrusters(
            this.gRemoteThrust, hh.x, hh.y, hh.heading, spd, 100,
            headTex.width * headScale, headTex.height * headScale, 6, 0.9, headIdx * 2.1, 1, 0.5,
          );
        }
        continue;
      }
      // ----- Normal hull: a single ship sprite at its real tier size -----
      const rs = this.remoteShips[shipIdx++];
      if (!rs) continue;
      const tex = this.remoteTexFor(p.tier);
      if (!tex) {
        rs.visible = false;
        continue;
      }
      rs.texture = tex;
      rs.visible = true;
      const sizeMul = HULL_SIZE_MUL[Math.min(p.tier, 5)] ?? 1;
      rs.scale.set((MOTHERSHIP.spriteWorldHeight / tex.height) * sizeMul);
      rs.position.set(p.x, p.y);
      rs.rotation = p.heading - Math.PI / 2;
      const spd = Math.hypot(p.vx, p.vy);
      if (spd > 8) {
        this.drawThrusters(
          this.gRemoteThrust,
          p.x,
          p.y,
          p.heading,
          spd,
          100,
          rs.width,
          rs.height,
          Math.min(p.tier + 1, 6),
          0.9,
          shipIdx * 2.1,
          1,
          p.tier >= 5 ? 0.42 : p.tier >= 4 ? 0.5 : 1,
          p.tier >= 5 ? MKVI_BTS_THRUSTERS : undefined,
        );
      }
    }
    // Hide every pooled sprite we didn't use this frame.
    for (let i = shipIdx; i < this.remoteShips.length; i++) this.remoteShips[i]!.visible = false;
    for (let i = headIdx; i < this.remoteWormHeads.length; i++) this.remoteWormHeads[i]!.visible = false;
    for (let i = bodyIdx; i < this.remoteWormBodies.length; i++) this.remoteWormBodies[i]!.visible = false;
    for (let i = tailIdx; i < this.remoteWormTails.length; i++) this.remoteWormTails[i]!.visible = false;
  }

  /** Faint transparent contrails streaming from both wingtips of a fighter. */
  private drawWingTrails(
    g: Graphics,
    cx: number,
    cy: number,
    heading: number,
    speed: number,
    refSpeed: number,
    spriteW: number,
    spriteH: number,
  ): void {
    const fx = Math.cos(heading);
    const fy = Math.sin(heading);
    const bx = -fx;
    const by = -fy;
    const px = -fy;
    const py = fx;
    const halfH = spriteH * 0.5;
    const halfW = spriteW * 0.5;
    const intensity = Math.max(0.12, Math.min(1, speed / refSpeed));
    const lat = halfW * 0.92; // wingtip lateral offset
    const fwd = halfH * 0.05;
    const len = halfH * (0.7 + intensity * 2.6);
    const w0 = Math.max(1, halfW * 0.12);
    const ppx = -by;
    const ppy = bx;
    for (const side of [1, -1]) {
      const wx = cx + px * lat * side + fx * fwd;
      const wy = cy + py * lat * side + fy * fwd;
      const ex = wx + bx * len;
      const ey = wy + by * len;
      g.poly([wx + ppx * w0, wy + ppy * w0, wx - ppx * w0, wy - ppy * w0, ex, ey]).fill({
        color: 0x8fd4ff,
        alpha: 0.05 + 0.06 * intensity,
      });
      g.moveTo(wx, wy).lineTo(ex, ey).stroke({
        width: Math.max(0.5, halfW * 0.05),
        color: 0xdff2ff,
        alpha: 0.12 + 0.1 * intensity,
      });
    }
  }

  /** Icons over trade-hub (🛒) and mechanical (🔧) planets — in the system view
   * over each service planet, and in the planet view over the central hub. */
  /** System-map planets, with the Eater's planet rendered as an exploded wreck + a
   * floating EOW-head marker while the boss still lives. */
  private syncSystemPlanets(state: GameState): void {
    const eid = state.eaterPlanetId;
    const ex = this.explodedPlanetSprite;
    const icon = this.eaterMapIcon;
    const ep = eid ? state.system.planets.find((p) => p.id === eid) : null;
    if (ep && ex) {
      this.planetField.sync(state.system.planets.filter((p) => p.id !== eid));
      const tex =
        this.explodedPlanetTextures[state.explodedFrame % this.explodedPlanetTextures.length];
      if (tex) ex.texture = tex;
      ex.visible = true;
      ex.position.set(ep.pos.x, ep.pos.y);
      ex.scale.set((ep.radius * 2 * WORM.explodedPlanetScale) / (tex ? tex.height : 256));
      if (icon) {
        const show = !state.eaterDefeated && this.wormHeadTex != null;
        icon.visible = show;
        if (show) {
          icon.position.set(ep.pos.x, ep.pos.y - ep.radius * WORM.explodedPlanetScale - 24);
          icon.scale.set(48 / this.wormHeadTex!.height);
        }
      }
    } else {
      this.planetField.sync(state.system.planets);
      if (ex) ex.visible = false;
      if (icon) icon.visible = false;
    }
  }

  private drawServiceIcons(state: GameState): void {
    let idx = 0;
    const g = this.gTutorial;
    g.clear();
    const showTut = state.tutorialHighlightPlanets && state.layer === "system";
    const ph = (performance.now() % 1400) / 1400; // 0..1 pulse
    if (state.layer === "system") {
      for (const p of state.system.planets) {
        if (p.service === "none" && !p.rogue) continue;
        if (showTut && p.service !== "none") {
          g.circle(p.pos.x, p.pos.y, p.radius + 8 + ph * 30).stroke({
            width: 3,
            color: 0xffd24a,
            alpha: 0.85 * (1 - ph),
          });
          g.circle(p.pos.x, p.pos.y, p.radius + 9).stroke({
            width: 2,
            color: 0xffd24a,
            alpha: 0.55,
          });
        }
        this.placeServiceIcon(
          idx++,
          p.rogue ? "rogue" : p.service,
          p.pos.x,
          p.pos.y - p.radius - 6,
        );
      }
    } else if (state.planetLayer && state.planetLayer.service !== "none") {
      const L = state.planetLayer;
      this.placeServiceIcon(
        idx++,
        L.service,
        L.planet.pos.x,
        L.planet.pos.y - L.planet.radius - 10,
      );
    }
    for (let i = idx; i < this.serviceIcons.length; i++) {
      this.serviceIcons[i]!.visible = false;
    }
  }

  private placeServiceIcon(
    idx: number,
    service: string,
    x: number,
    y: number,
  ): void {
    let t = this.serviceIcons[idx];
    if (!t) {
      t = new Text({
        text: "",
        style: { fontFamily: "system-ui, sans-serif", fontSize: 28 },
      });
      t.anchor.set(0.5, 1);
      this.serviceLayer.addChild(t);
      this.serviceIcons[idx] = t;
    }
    t.text =
      service === "rogue"
        ? "🏴‍☠️"
        : service === "mechanical"
          ? "🔧"
          : service === "tavern"
            ? "🍺"
            : "🛒";
    t.visible = true;
    t.position.set(x, y);
  }

  private drawMiniBar(
    g: Graphics,
    x: number,
    y: number,
    frac: number,
    color: number,
  ): void {
    const w = 26;
    const h = 3;
    g.rect(x - w / 2, y, w, h).fill({ color: 0x0a0f1a, alpha: 0.7 });
    g.rect(x - w / 2, y, w * Math.max(0, Math.min(1, frac)), h).fill({
      color,
      alpha: 0.95,
    });
  }

  private spawnFloater(e: FloaterEvent): void {
    const n = Math.round(e.amount);
    if (e.kind === "mineral") {
      this.floaters.spawn(e.x, e.y, `+${n}`, 0x9fe6ff, { size: 13 });
    } else if (e.kind === "xp") {
      this.floaters.spawn(e.x, e.y, `+${n} XP`, 0xc9a2ff, {
        size: 15,
        vy: -42,
        life: 1.1,
      });
    } else if (e.kind === "loot") {
      this.floaters.spawn(e.x, e.y, e.label ?? "Item", 0xffd27a, {
        size: 16,
        vy: -32,
        life: 1.6,
      });
    } else {
      this.floaters.spawn(e.x, e.y, `LEVEL ${n}!`, 0xffcf5c, {
        size: 22,
        vy: -40,
        life: 1.8,
      });
    }
  }

  /** Small moons orbiting each planet in the system view (count matches the
   * planet layer). Drawn into gOrbits after the orbit rings. */
  private drawSystemMoons(
    planets: {
      pos: { x: number; y: number };
      moons: { angle: number; distance: number; radius: number }[];
    }[],
  ): void {
    const g = this.gOrbits;
    for (const p of planets) {
      for (const m of p.moons) {
        g.circle(p.pos.x, p.pos.y, m.distance).stroke({
          width: 1,
          color: 0x3a4a66,
          alpha: 0.18,
        });
        const mx = p.pos.x + Math.cos(m.angle) * m.distance;
        const my = p.pos.y + Math.sin(m.angle) * m.distance;
        g.circle(mx, my, m.radius).fill({ color: 0xc2cadb, alpha: 0.95 });
      }
    }
  }

  /** Two faint counter-rotating rings around each rogue world that still hosts a live
   * ring-station — the solar-view echo of the boss (gone once it's defeated). */
  private drawStationRings(state: GameState): void {
    const ids = state.stationRingIds;
    if (ids.length === 0) return;
    const g = this.gOrbits;
    const t = performance.now() / 1000;
    for (const p of state.system.planets) {
      if (!ids.includes(p.id)) continue;
      this.drawSpinRing(g, p.pos.x, p.pos.y, p.radius + 28, t * 0.6, 0x9a6b3a); // outer, CW
      this.drawSpinRing(g, p.pos.x, p.pos.y, p.radius + 17, -t * 0.9, 0xb98a4a); // inner, CCW
    }
  }

  /** Jump cutscene: smear the star + every planet into a thin tapered streak pulled toward the
   * rift, thinning and fading as the collapse completes (spaghettification). */
  private drawWarpSpaghetti(state: GameState): void {
    const p = state.warpProgress; // 0..1
    if (p <= 0) return;
    const g = this.gWarpFx;
    const r = state.warpRift;
    // Every streak runs PARALLEL, along the ship's heading (warp-speed lines) — the bodies are NOT
    // pulled toward the rift point; they all stretch the same direction the ship faces.
    const heading = r ? r.heading : state.mothership.heading;
    const ux = Math.cos(heading);
    const uy = Math.sin(heading);
    const px = -uy; // perpendicular → streak width
    const py = ux;
    const sys = state.system;
    const bodies: { x: number; y: number; rad: number; color: number }[] = [
      { x: sys.star.pos.x, y: sys.star.pos.y, rad: sys.star.radius, color: 0xffcf7a },
    ];
    for (const pl of sys.planets) {
      bodies.push({ x: pl.pos.x, y: pl.pos.y, rad: pl.radius, color: pl.color });
    }
    const ease = p * p; // the stretch accelerates
    for (const b of bodies) {
      const len = b.rad * 2 + (b.rad * 5 + 520) * ease; // stretch into a long parallel streak
      const w = Math.max(0.6, b.rad * (1 - 0.85 * p)); // thins to a noodle
      const hx = b.x + ux * len; // head — forward along the ship's heading
      const hy = b.y + uy * len;
      const a = 1 - p * 0.55;
      // tapered noodle: wide at the body → point at the forward end.
      g.poly([b.x + px * w, b.y + py * w, b.x - px * w, b.y - py * w, hx, hy]).fill({
        color: b.color,
        alpha: 0.5 * a,
      });
      g.moveTo(b.x, b.y)
        .lineTo(hx, hy)
        .stroke({ width: Math.max(1, w * 0.8), color: 0xffffff, alpha: 0.3 * a });
      g.moveTo(b.x, b.y)
        .lineTo(hx, hy)
        .stroke({ width: Math.max(0.5, w * 0.4), color: b.color, alpha: 0.8 * a });
    }
  }

  /** Jump cutscene: the rift portal the ship dives into / emerges from (reuses the Mk VIII art,
   * sized to the mothership). Closed slit → open eye via `open`, faded by `fade`. */
  private drawWarpRift(state: GameState): void {
    const sp = this.warpRiftSprite;
    if (!sp) return;
    const r = state.warpRift;
    if (!r || r.fade <= 0.001) {
      sp.visible = false;
      return;
    }
    const tex = r.open > 0.02 ? this.riftOpenedTex : this.riftClosedTex;
    if (!tex) {
      sp.visible = false;
      return;
    }
    sp.texture = tex;
    sp.visible = true;
    sp.position.set(r.cx, r.cy);
    sp.rotation = r.heading + Math.PI / 2; // slit runs perpendicular to the ship's travel
    const base = r.worldWidth / tex.width; // slit length in world units = mothership-sized
    const openScale = 0.06 + 0.94 * r.open; // closed slit → full open eye
    sp.scale.set(base, base * openScale);
    sp.alpha = r.fade;
    sp.tint = 0xffffff;
  }

  /** A ring rendered as short arc dashes rotated by `phase`, so the spin reads at a glance. */
  private drawSpinRing(
    g: Graphics,
    cx: number,
    cy: number,
    r: number,
    phase: number,
    color: number,
  ): void {
    g.circle(cx, cy, r).stroke({ width: 1, color, alpha: 0.16 });
    const SEG = 10;
    const arc = (Math.PI * 2) / SEG;
    for (let i = 0; i < SEG; i++) {
      const a0 = phase + i * arc;
      g.moveTo(cx + Math.cos(a0) * r, cy + Math.sin(a0) * r);
      g.arc(cx, cy, r, a0, a0 + arc * 0.55);
    }
    g.stroke({ width: 2, color, alpha: 0.7 });
  }

  private drawOrbits(
    radii: number[],
    cx: number,
    cy: number,
    moons:
      | {
          pos: { x: number; y: number };
          distance: number;
          ore: number;
          subMoons: { distance: number; ore: number }[];
        }[]
      | null,
  ): void {
    const g = this.gOrbits;
    g.clear();
    for (const r of radii) {
      g.circle(cx, cy, r).stroke({ width: 1, color: 0x33507a, alpha: 0.35 });
    }
    if (moons) {
      for (const m of moons) {
        if (m.ore <= 0) continue; // destroyed moon → its rings vanish too
        g.circle(cx, cy, m.distance).stroke({ width: 1, color: 0x33507a, alpha: 0.3 });
        for (const s of m.subMoons) {
          if (s.ore <= 0) continue;
          g.circle(m.pos.x, m.pos.y, s.distance).stroke({
            width: 1,
            color: 0x33507a,
            alpha: 0.22,
          });
        }
      }
    }
  }

  private drawShipAndBeams(state: GameState): void {
    const m = state.mothership;
    const sg = this.gShip;
    sg.clear();
    this.gThrustPlayer.clear();

    // Expanding explosion rings from mined-out bodies.
    for (const r of this.rings) {
      if (r.big) {
        const k = r.age / BIG_RING_LIFE; // 0 → 1
        // bright flash core
        sg.circle(r.x, r.y, r.radius * (1 + k * 1.3)).fill({
          color: 0xfff0c0,
          alpha: 0.4 * (1 - k),
        });
        // main shockwave + a trailing ring
        sg.circle(r.x, r.y, r.radius * (0.6 + k * 4.2)).stroke({
          width: 6 * (1 - k),
          color: 0xffb85a,
          alpha: 0.85 * (1 - k),
        });
        sg.circle(r.x, r.y, r.radius * (0.3 + k * 3.1)).stroke({
          width: 3 * (1 - k),
          color: 0xffffff,
          alpha: 0.6 * (1 - k),
        });
      } else if (r.blue) {
        // Homing-missile blast — a blue flash core + expanding blue shockwave.
        const k = r.age / RING_LIFE; // 0 → 1
        sg.circle(r.x, r.y, r.radius * (0.9 + k * 0.9)).fill({
          color: 0x8fd0ff,
          alpha: 0.4 * (1 - k),
        });
        sg.circle(r.x, r.y, r.radius * (1 + k * 1.9)).stroke({
          width: 3 * (1 - k),
          color: 0x49a6ff,
          alpha: 0.75 * (1 - k),
        });
        sg.circle(r.x, r.y, r.radius * (0.6 + k * 1.3)).stroke({
          width: 1.5 * (1 - k),
          color: 0xdff2ff,
          alpha: 0.6 * (1 - k),
        });
      } else {
        const k = r.age / RING_LIFE; // 0 → 1
        sg.circle(r.x, r.y, r.radius * (1 + k * 1.9)).stroke({
          width: 2.5 * (1 - k),
          color: 0xffd27a,
          alpha: 0.65 * (1 - k),
        });
      }
    }

    // Service planet interaction zone — fly inside the ring to open the services.
    if (state.layer === "planet" && state.planetLayer?.service !== "none" && state.planetLayer) {
      const L = state.planetLayer;
      const near = state.nearServicePlanet;
      const col =
        L.service === "mechanical"
          ? 0xffcf5c
          : L.service === "tavern"
            ? 0xffb347
            : 0x56e39f;
      sg.circle(
        L.planet.pos.x,
        L.planet.pos.y,
        L.planet.radius + SERVICE.interactMargin,
      ).stroke({
        width: near ? 3 : 1.5,
        color: col,
        alpha: near ? 0.7 : 0.35,
      });
    }

    // Range indicator — toggled from the HUD. Shows the hull mining cone, plus
    // the drone deploy circle once drones are unlocked.
    if (state.showMiningRange && state.layer === "planet") {
      if (state.dronesActive) {
        sg.circle(m.pos.x, m.pos.y, state.droneRange)
          .fill({ color: 0x56e39f, alpha: 0.04 })
          .stroke({ width: 1.5, color: 0x56e39f, alpha: 0.35 });
      }
      // Cannon firing range (Mk II+), so the player can see where turrets engage.
      if (state.purchasedUpgrades.has("mk2")) {
        sg.circle(m.pos.x, m.pos.y, TURRET.range)
          .stroke({ width: 1.5, color: 0xff8a5a, alpha: 0.4 });
      }
      const range = state.miningRangeWorld;
      const half = (MINING_LASER.coneHalfAngleDeg * Math.PI) / 180;
      sg.moveTo(m.pos.x, m.pos.y)
        .arc(m.pos.x, m.pos.y, range, m.heading - half, m.heading + half)
        .lineTo(m.pos.x, m.pos.y)
        .fill({ color: 0x39c6ff, alpha: 0.07 })
        .stroke({ width: 1.5, color: 0x39c6ff, alpha: 0.45 });
    }

    if (state.moveBodyId && state.planetLayer) {
      const b = state.planetLayer.byId(state.moveBodyId);
      if (b) {
        sg.circle(b.pos.x, b.pos.y, b.radius + 12).stroke({
          width: 2,
          color: 0x4fc3f7,
          alpha: 0.75,
        });
      }
    }

    if (this.shipSprite && !state.isWorm) {
      this.shipSprite.visible = true;
      // Hull tier by highest milestone owned: 0 Mk I … 3 Mk IV, 4 Mk V (by path), 5 Mk VI.
      const path = state.mkvPath;
      const tier =
        state.purchasedUpgrades.has("mkvi_bts") || state.purchasedUpgrades.has("mkvi_swl")
          ? 5
          : path !== "none"
            ? 4
            : state.purchasedUpgrades.has("mk4")
              ? 3
              : state.purchasedUpgrades.has("mk3")
                ? 2
                : state.purchasedUpgrades.has("mk2")
                  ? 1
                  : 0;
      // Pick texture + base scale (Mk VI has its own hull; Mk V has two path hulls;
      // lower tiers index by tier).
      const mkvTex = path === "swarmlord" ? this.shipTexVSwl : this.shipTexVBts;
      const mkviTex = path === "swarmlord" ? (state.isMkviiSwl ? this.shipTexVIISwl : this.shipTexVISwl) : this.shipTexVIBts;
      const tex = tier >= 5 ? mkviTex : tier >= 4 ? mkvTex : this.shipTextures[tier];
      const baseScale =
        tier >= 5
          ? path === "swarmlord"
            ? (state.isMkviiSwl ? this.baseScaleVIISwl * MKVI.swl7SizeMul : this.baseScaleVISwl * MKVI.swlSizeMul) // Mk VI +10% / Mk VII +32%
            : this.baseScaleVIBts
          : tier >= 4
            ? path === "swarmlord"
              ? this.baseScaleVSwl
              : this.baseScaleVBts
            : (this.baseShipScales[tier] ?? 1);
      if (tier !== this.hullTier || path !== this.hullPath || state.isMkviiSwl !== this.hullMkviiSwl) {
        this.hullTier = tier;
        this.hullPath = path;
        this.hullMkviiSwl = state.isMkviiSwl;
        if (tex) this.shipSprite.texture = tex;
      }
      const sizeMul = HULL_SIZE_MUL[tier] ?? 1; // Mk II +20% … Mk V ×6.3
      // 75% smaller in the interplanetary (system) view — but full-size during the jump
      // cutscene (so the ship fits the mothership-sized rift it flies through), and the
      // warpShipScale shrinks it into / grows it out of the rift.
      const systemMini = state.layer === "system" && !state.warpActive ? 0.25 : 1;
      const scale = baseScale * sizeMul * systemMini;
      this.shipSprite.scale.set(scale * state.warpShipScale);
      this.shipSprite.position.set(m.pos.x, m.pos.y);
      // Use the true heading (not a smoothed rotation) so hull-mounted turrets
      // stay locked to their ports. The hull turn-rate already makes it gradual.
      this.shipSprite.rotation = m.heading - Math.PI / 2;
      if (!state.playerDead && m.hp > 0) {
        // Back-mounted thrusters: Mk I → 1, Mk II → 2, Mk III → 3, Mk IV → 4.
        // Idle → off; moving → full; moving while mining → mid.
        const spd = Math.hypot(m.vel.x, m.vel.y);
        const moving = spd > 8;
        const mining = state.miningTargets.length > 0;
        const engineI = !moving ? 0 : mining ? 0.5 : 1;
        this.drawThrusters(
          this.gThrustPlayer,
          m.pos.x,
          m.pos.y,
          m.heading,
          spd,
          100,
          this.shipSprite.width,
          this.shipSprite.height,
          tier + 1,
          0.9,
          7,
          engineI,
          tier >= 5 ? 0.42 : tier >= 4 ? 0.5 : 1, // Mk V/VI: tame the (otherwise huge) plumes
          state.mkvPath === "swarmlord"
            ? tier >= 5
              ? (state.isMkviiSwl ? MKVII_SWL_THRUSTERS : MKVI_SWL_THRUSTERS) // Mk VI / Mk VII Swarm-lord engines
              : SWL_THRUSTERS
            : tier >= 5
              ? MKVI_BTS_THRUSTERS // Mk VI Battleship: clustered stern engine bank
              : undefined,
          state.isMkviiSwl, // Mk VII SWL: purple engine plumes
        );
      }
    } else if (this.shipSprite) {
      this.shipSprite.visible = false; // Mk VII renders as a worm, not a hull
    }
    this.drawPlayerWorm(state);
    this.drawRemotePlayers(state); // Central Hub: the other pilots' ships (hidden otherwise)

    // Shield bubble around the hull — scales with the hull tier, brighter with more
    // shield and on-hit flash. Uses the true hullTier (0..5) so the DRAWN bubble
    // matches the sim's shieldRadius hitbox (both index SHIP_DEFENSE.shieldBubbleMul).
    if (m.maxShield > 0 && m.shield > 0 && !state.isWorm) {
      const frac = m.shield / m.maxShield;
      const fl = state.shieldFlash;
      const tier = state.hullTier;
      const rBubble =
        (SHIP_DEFENSE.collisionRadius + 16) *
        (SHIP_DEFENSE.shieldBubbleMul[tier] ?? 1);
      const shStroke = {
        width: 1.5 + fl * 2.5,
        color: 0x6cc6ff,
        alpha: 0.14 + frac * 0.22 + fl * 0.5,
      };
      if (tier >= 2) {
        // Mk III / Mk IV: an OVAL bubble elongated along the hull, so it hugs the
        // bigger, longer ship rather than sitting inside a round bubble.
        const fx = Math.cos(m.heading);
        const fy = Math.sin(m.heading);
        const px = -fy;
        const py = fx;
        const rl = rBubble * 1.32; // along the hull (forward axis)
        const rw = rBubble * 0.9; // across the hull
        const pts: number[] = [];
        for (let i = 0; i < 44; i++) {
          const a = (i / 44) * Math.PI * 2;
          const cl = Math.cos(a) * rl;
          const cw = Math.sin(a) * rw;
          pts.push(m.pos.x + fx * cl + px * cw, m.pos.y + fy * cl + py * cw);
        }
        sg.poly(pts).stroke(shStroke);
      } else {
        sg.circle(m.pos.x, m.pos.y, rBubble).stroke(shStroke);
      }
    }

    if (m.moveTarget) {
      sg.circle(m.moveTarget.x, m.moveTarget.y, 9).stroke({
        width: 2,
        color: 0x39ff14,
        alpha: 0.5,
      });
    }

    if (state.miningTargets.length > 0) {
      const fx = Math.cos(m.heading);
      const fy = Math.sin(m.heading);
      const perpx = -fy;
      const perpy = fx;
      const F = MINING_LASER.gunForward;
      const S = MINING_LASER.gunSpread;
      // The two drill tips — every beam still originates from these same points.
      const gunA = { x: m.pos.x + fx * F + perpx * S, y: m.pos.y + fy * F + perpy * S };
      const gunB = { x: m.pos.x + fx * F - perpx * S, y: m.pos.y + fy * F - perpy * S };
      const flicker = 0.75 + Math.random() * 0.25;
      let beamIdx = 0;
      // Each beam runs from one of the two drill tips to its OWN wandering spot,
      // so multiple beams visibly strike different points on the target.
      for (const t of state.miningTargets) {
        for (const spot of t.impacts) {
          const gun = beamIdx % 2 === 0 ? gunA : gunB;
          beamIdx++;
          sg.moveTo(gun.x, gun.y)
            .lineTo(spot.x, spot.y)
            .stroke({ width: 4, color: 0x39c6ff, alpha: 0.12 * flicker });
          sg.moveTo(gun.x, gun.y)
            .lineTo(spot.x, spot.y)
            .stroke({ width: 1.2, color: 0xeaffff, alpha: 0.95 });
          sg.circle(spot.x, spot.y, 2.0 * flicker).fill({ color: 0xffffff, alpha: 0.8 });
        }
      }
      sg.circle(gunA.x, gunA.y, 2.4).fill({ color: 0xbdefff, alpha: 0.9 });
      sg.circle(gunB.x, gunB.y, 2.4).fill({ color: 0xbdefff, alpha: 0.9 });
    }
  }

  destroy(): void {
    if (!this.initialized) return;
    this.initialized = false;
    try {
      this.app.destroy(true, { children: true });
    } catch (err) {
      console.warn("[space-swarm] Pixi destroy warning:", err);
    }
  }
}
